import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { toast } from 'sonner';
import * as Api from '../../../extViewmodels';
import { CsmMeetingTypes, IAccount, IUser, NormalizedStages } from '../../../extViewmodels';
import { RealMagicAccountIds } from '../../../extViewmodels/viewModels/index';
import { useDebounceCallback } from '../../../hooks/useDebounceValue';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useMeetingLogStorage } from '../../../hooks/useMeetingLogStorage';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { useErrorMessages } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { createContentStateWithHtmlStringValue } from '../../../models/utils/richContentUtils';
import { useAccountProfileCreateMutation } from '../../../queries/AccountProfile/useAccountProfileCreateMutation';
import {
	invalidateAccountProfileQueries,
	useAccountProfileQuery,
} from '../../../queries/AccountProfile/useAccountProfileQuery';
import { useAccountProfileUpdateMutation } from '../../../queries/AccountProfile/useAccountProfileUpdateMutation';
import { useBrandingSettingsQuery } from '../../../queries/AccountProfile/useBrandingSettingsQuery';
import { useAdminAccountFilterQuery } from '../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { useAdminAccountQuery } from '../../../queries/Admin/Account/useAdminAccountQuery';
import { useAdminAccountWebsiteUrlMutation } from '../../../queries/Admin/Account/useAdminAccountWebsiteUrlMutation';
import { useAdminAccountProfileGenerateMutation } from '../../../queries/Admin/AccountProfile/useAdminAccountProfileGenerateMutation';
import {
	useAdminAccountMeetingNoteMutation,
	useAdminCalendarMeetingTranscriptsMutation,
	useAdminContentNotesGenerateMutation,
	useAdminContentNotesSaveMutation,
	useAdminMonitorComplaintsMutation,
	useAdminTranscriptSummarizeMutation,
} from '../../../queries/Admin/Calendar';
import { IMeetingReference, MeetingReferenceSource } from '../../../models/AdminModels';
import { useLoadAllUsers } from '../../../queries/User/useLoadAllUsers';
import { ConfirmationTab } from '../../hooks/useMeetingNoteRecording';
import { getInitials } from '../../../utils/Support/supportHelpers';
import { industrySupportsSubverticals } from '../../../models/utils/industryUtils';
import { hasBrandingSettings } from '../../../utils/hasBrandingSettings';
import { hasContentPreferences } from '../../../utils/hasContentPreferences';
import * as AdminApi from '../../../viewmodels/AdminViewModels';
import {
	GeographicScope,
	ICalendarMeetingAttendee,
	ICalendarMeetingInfo,
	ICalendarMeetingsResponse,
	ICalendarMeetingWithTranscripts,
	ITranscriptMatch,
} from '../../../viewmodels/CalendarViewModels';
import { DayPicker } from '../../../web/components/DayPicker';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { RichContentDocumentEditor } from '../../../web/components/richContent/RichContentDocumentEditor';
import { markdownToEditorHtml } from '../../../web/components-v2/ai/markdownToEditorHtml';
import { Avatar } from '../../../web/components-v2/Avatar';
import { Button } from '../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
	ComboboxStatus,
} from '../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../web/components-v2/Dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { titles } from '../../../web/styles/colors';
import { bs } from '../../../web/styles/styles';
import { BrandSettingsTab } from '../../components/MeetingNoteRecordingFlow/BrandSettingsTab';
import {
	ContentPlanOutputMix,
	DEFAULT_MONTH_COUNT,
	defaultStartMonth,
} from '../../components/MeetingNoteRecordingFlow/contentPlanOptions';
import { ContentTeamTab } from '../../components/MeetingNoteRecordingFlow/ContentTeamTab';
import { SubverticalSettingsTab } from '../../components/MeetingNoteRecordingFlow/SubverticalSettingsTab';
import { normalizeWebsiteUrl } from '../../components/MeetingNoteRecordingFlow/websiteUrl';

// Custom CSS for content team editor with more padding
const ContentTeamEditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: "Sora", sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

// Custom CSS for recap email editor
const RecapEmailEditorCSS = `
	body {
		background: transparent;
		color: ${titles};
		font-family: Arial, sans-serif;
		font-size: 14px;
		margin: 0;
		padding: 16px 20px;
	}
`;

import {
	getContentHtmlStringValueFromRawRichTextContentState,
	IRichContentEditorState,
} from '../../../viewmodels/AppViewModels';
import { AskLevDialog } from '../../components/AskLevDialog';
import { styleSheet } from './styles';
import { formatMeetingSchedule, getAttendeeDisplayName, parseMeetingDate } from './utils';

const DEFAULT_MEETING_DURATION_MINUTES = 60;
const DEFAULT_NO_SHOW_DURATION_MINUTES = 30;

// Max transcript length accepted by the complaint-monitoring endpoint.
const MONITOR_COMPLAINTS_MAX_TRANSCRIPT_CHARS = 500_000;

type TeamType = 'SuccessClientMeeting' | 'ImplementationMeeting';
const TEAM_TYPE_STORAGE_KEY = 'meeting-notes-team-type';
const TEAM_TYPE_OPTIONS = [
	{ value: 'SuccessClientMeeting' as const, label: 'Success Specialist' },
	{ value: 'ImplementationMeeting' as const, label: 'Implementation' },
];

// Geographic scope drives how the web team's SEO deep research is oriented. The specialist must
// choose intentionally before generating the web team summary (the API defaults to Local if omitted).
// Keyed by GeographicScope so adding a new union member is a compile error until its copy is defined,
// keeping the type and the rendered options from drifting apart.
const GEOGRAPHIC_SCOPE_DETAILS: Record<GeographicScope, { label: string; description: string }> = {
	Local: {
		label: 'Local',
		description: 'Single city or metro area. Proximity is the primary ranking lever. (Use for most clients.)',
	},
	Regional: {
		label: 'Regional',
		description:
			'Multiple distinct markets, typically statewide or multi-state (e.g., a firm operating across several states).',
	},
	National: {
		label: 'National',
		description: 'Nationwide reach (e.g., a national non-profit or a large multi-state firm).',
	},
};
const GEOGRAPHIC_SCOPE_OPTIONS = (Object.keys(GEOGRAPHIC_SCOPE_DETAILS) as GeographicScope[]).map(value => ({
	value,
	...GEOGRAPHIC_SCOPE_DETAILS[value],
}));

const MEETING_NOTE_EDITOR_CLASS = 'meeting-note-editor';

// Deliberately looser than the predicate that binds the account in resolveAccountFromAttendees: this
// one keeps attendees with no accountId, because an unrecognized external guest is still useful
// context when judging whether the bound account is right. Changing one is not a reason to change
// the other.
const isExternalAttendee = (attendee: ICalendarMeetingAttendee) =>
	!attendee.isOrganizer && !(attendee.accountId && RealMagicAccountIds.has(attendee.accountId));

export function LogMeetingNotes() {
	const id = React.useId();
	const userSession = useAdminUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('LogMeetingNotes');

	// Inject scoped toolbar border style (toolbar is outside the TinyMCE iframe, so contentRawCss can't reach it)
	React.useEffect(() => {
		const style = document.createElement('style');
		style.textContent = `.${MEETING_NOTE_EDITOR_CLASS} .tox-toolbar__primary { border-bottom: 1px solid #D8D8D8 !important; }`;
		document.head.appendChild(style);
		return () => {
			document.head.removeChild(style);
		};
	}, []);

	// State for user selection
	const [selectedUser, setSelectedUser] = React.useState<IUser>(userSession.user);
	const [userSearchValue, setUserSearchValue] = React.useState(getDisplayName(userSession.user));
	const {
		users,
		isLoading: isLoadingUsers,
		error: usersError,
	} = useLoadAllUsers({
		pageSize: 500,
	});
	const activeUsers = React.useMemo(() => {
		if (!users.length) return [userSession.user];

		return users
			.filter((user: IUser) => user.activationStatus === 'ACTIVE')
			.sort((a: IUser, b: IUser) => getDisplayName(a).localeCompare(getDisplayName(b)));
	}, [users, userSession]);

	// Account search with debounce
	const [debouncedAccountSearch, setDebouncedAccountSearch] = React.useState('');
	const debouncedAccountSearchCallback = useDebounceCallback((value: string) => {
		setDebouncedAccountSearch(value);
	}, 300);
	const handleAccountSearchValueChange = React.useCallback(
		(value: string) => {
			setAccountSearchValue(value);
			debouncedAccountSearchCallback(value);
		},
		[debouncedAccountSearchCallback]
	);

	// Account filter for search
	const accountFilterRequest = React.useMemo(() => {
		const criteria: AdminApi.IAccountFilterCriteria[] = [];

		// Add name filter when searching, just like in /accounts
		if (debouncedAccountSearch && debouncedAccountSearch.length > 0) {
			criteria.push({
				property: AdminApi.AccountFilterCriteriaProperty.Name,
				value: debouncedAccountSearch,
			});
		}

		return {
			criteria,
			op: Api.FilterOperator.And,
		} as AdminApi.IAccountsFilterRequest;
	}, [debouncedAccountSearch]);

	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilterRequest,
		autocompleteFragment: debouncedAccountSearch || undefined,
		pageSize: 50,
	});

	const accounts = React.useMemo(() => {
		if (!accountsQuery.data) return [];
		return accountsQuery.data.pages.flatMap(page => page.values || []);
	}, [accountsQuery.data]);

	// State for date - default to today
	const [selectedDate, setSelectedDate] = React.useState<Date>(new Date());
	const [isDatePickerOpen, setIsDatePickerOpen] = React.useState(false);

	// State for meetings data
	const [meetingsData, setMeetingsData] = React.useState<ICalendarMeetingsResponse | null>(null);
	const [isLoadingMeetings, setIsLoadingMeetings] = React.useState<boolean>(false);
	const [meetingsError, setMeetingsError] = React.useState<string | null>(null);

	// State for meetings display
	const [selectedMeeting, setSelectedMeeting] = React.useState<ICalendarMeetingInfo | null>(null);
	const [selectedMeetingWithTranscripts, setSelectedMeetingWithTranscripts] =
		React.useState<ICalendarMeetingWithTranscripts | null>(null);
	const [selectedTranscript, setSelectedTranscript] = React.useState<ITranscriptMatch | null>(null);
	const [selectedTranscriptIndex, setSelectedTranscriptIndex] = React.useState<number | null>(null);
	const [isTranscriptDialogOpen, setIsTranscriptDialogOpen] = React.useState(false);
	const [expandedTranscripts, setExpandedTranscripts] = React.useState<Set<number>>(new Set());
	const [editedTranscripts, setEditedTranscripts] = React.useState<Map<number, string>>(new Map());
	const [askLevTranscript, setAskLevTranscript] = React.useState<ITranscriptMatch | null>(null);

	// State for meeting note form
	const [showMeetingNoteForm, setShowMeetingNoteForm] = React.useState(false);
	const [didSkipMeetingNote, setDidSkipMeetingNote] = React.useState(false);
	const [selectedAccount, setSelectedAccount] = React.useState<IAccount | null>(null);
	const [accountSearchValue, setAccountSearchValue] = React.useState('');
	const [accountIdToFetch, setAccountIdToFetch] = React.useState<string | null>(null);

	// Query to fetch full account data when resolving from attendees
	const fullAccountQuery = useAdminAccountQuery({
		accountId: accountIdToFetch || '',
		enabled: !!accountIdToFetch,
	});

	// Set selected account when full account data is fetched
	React.useEffect(() => {
		if (fullAccountQuery.data) {
			setSelectedAccount(fullAccountQuery.data);
			setAccountSearchValue(fullAccountQuery.data.companyName || '');
			setAccountStage(fullAccountQuery.data.additionalInfo?.stage || null);
			setAccountIdToFetch(null); // Reset to prevent re-fetching
		}
	}, [fullAccountQuery.data]);
	const [meetingNoteContentState, setMeetingNoteContentState] = React.useState<IRichContentEditorState | null>(null);
	const [meetingType, setMeetingType] = React.useState<CsmMeetingTypes | null>(null);
	const [meetingStatus, setMeetingStatus] = React.useState('Completed');
	const [accountStage, setAccountStage] = React.useState<string | null>(null);
	const [duration, setDuration] = React.useState<number>(DEFAULT_MEETING_DURATION_MINUTES);
	const [isOnboarding, setIsOnboarding] = React.useState(false);
	const [nextMeetingDate, setNextMeetingDate] = React.useState<Date | null>(null);
	const [showNextMeetingDatePicker, setShowNextMeetingDatePicker] = React.useState(false);
	const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
	const [isSavingNote, setIsSavingNote] = React.useState(false);

	// Team type selection (persisted in localStorage)
	const [selectedTeamType, setSelectedTeamType] = useLocalStorage<TeamType>({
		key: TEAM_TYPE_STORAGE_KEY,
		initialValue: 'SuccessClientMeeting',
	});

	// Track which meetings have had notes recorded (persisted in localStorage with 7-day TTL)
	const { isMeetingLogged, isMeetingNoShow, markMeetingLogged, markMeetingNoShow } = useMeetingLogStorage();

	// State for no show dialog
	const [isNoShowDialogOpen, setIsNoShowDialogOpen] = React.useState(false);
	const [noShowMeeting, setNoShowMeeting] = React.useState<ICalendarMeetingInfo | null>(null);
	const [noShowAccount, setNoShowAccount] = React.useState<IAccount | null>(null);
	const [noShowAccountSearchValue, setNoShowAccountSearchValue] = React.useState('');
	const [isSavingNoShow, setIsSavingNoShow] = React.useState(false);

	// State for web team confirmation
	const [showWebTeamConfirmation, setShowWebTeamConfirmation] = React.useState(false);
	const [webTeamSummary, setWebTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingWebTeamSummary, setIsGeneratingWebTeamSummary] = React.useState(false);

	// Web Team editable Deep Research fields
	const [webTeamBusinessType, setWebTeamBusinessType] = React.useState<string>('');
	const [webTeamLocation, setWebTeamLocation] = React.useState<string>('');
	const [webTeamWebsiteUrl, setWebTeamWebsiteUrl] = React.useState<string>('');
	// Required: the specialist must pick a geographic scope before generating the web team summary.
	const [webTeamGeographicScope, setWebTeamGeographicScope] = React.useState<GeographicScope | null>(null);

	// State for content team confirmation
	const [showContentTeamConfirmation, setShowContentTeamConfirmation] = React.useState(false);
	const [contentTeamSummary, setContentTeamSummary] = React.useState<string | null>(null);
	const [isGeneratingContentTeamSummary, setIsGeneratingContentTeamSummary] = React.useState(false);

	// Content Team editable fields
	const [contentTeamWebsiteUrl, setContentTeamWebsiteUrl] = React.useState<string>('');
	const [contentTeamContentState, setContentTeamContentState] = React.useState<IRichContentEditorState | null>(null);
	const [isSubmittingContentTeamNote, setIsSubmittingContentTeamNote] = React.useState(false);
	// Structured content plan intake. The specialist states these rather than leaving the ideation to
	// infer them from the transcript. https://github.com/Real-Magic/Levitate/issues/20255
	const [contentTeamOutputMix, setContentTeamOutputMix] = React.useState<ContentPlanOutputMix | null>(null);
	const [contentTeamStartMonth, setContentTeamStartMonth] = React.useState(defaultStartMonth());
	const [contentTeamMonthCount, setContentTeamMonthCount] = React.useState(DEFAULT_MONTH_COUNT);
	const [contentTeamNewsletterTemplateId, setContentTeamNewsletterTemplateId] = React.useState('');

	// Account Profile (AI Custom Content) confirmation state
	const [showAccountProfileConfirmation, setShowAccountProfileConfirmation] = React.useState(false);
	const [accountProfileWebsiteUrl, setAccountProfileWebsiteUrl] = React.useState<string>('');
	const [isGeneratingAccountProfile, setIsGeneratingAccountProfile] = React.useState(false);

	// Editable generated Account Profile fields
	const [generatedBusinessDescription, setGeneratedBusinessDescription] = React.useState('');
	const [generatedKeywords, setGeneratedKeywords] = React.useState('');
	const [generatedContentGoals, setGeneratedContentGoals] = React.useState('');
	const [generatedLocation, setGeneratedLocation] = React.useState('');
	const [generatedAddress, setGeneratedAddress] = React.useState('');
	const [generatedPhoneNumber, setGeneratedPhoneNumber] = React.useState('');
	const [generatedPronouns, setGeneratedPronouns] = React.useState('');
	const [generatedTonePreference, setGeneratedTonePreference] = React.useState('');
	const [isSavingAccountProfile, setIsSavingAccountProfile] = React.useState(false);

	// Brand Settings confirmation state
	const [showBrandSettingsConfirmation, setShowBrandSettingsConfirmation] = React.useState(false);
	const [brandSettingsWebsiteUrl, setBrandSettingsWebsiteUrl] = React.useState<string>('');
	const [isSavingBrandSettingsWebsiteUrl, setIsSavingBrandSettingsWebsiteUrl] = React.useState(false);
	const [brandSettingsWebsiteUrlSaved, setBrandSettingsWebsiteUrlSaved] = React.useState(false);

	// Industry & Subvertical confirmation state
	const [showSubverticalSettingsConfirmation, setShowSubverticalSettingsConfirmation] = React.useState(false);

	// Tab-based confirmation flow. ConfirmationTab is imported rather than redeclared so this page and
	// the extracted useMeetingNoteRecording flow cannot drift apart as tabs are added.
	const [activeConfirmationTab, setActiveConfirmationTab] = React.useState<ConfirmationTab | null>(null);
	const [completedTabs, setCompletedTabs] = React.useState<Set<ConfirmationTab>>(new Set());

	// Send Recap Email confirmation state
	const [showRecapEmailConfirmation, setShowRecapEmailConfirmation] = React.useState(false);
	const [recapEmailContentState, setRecapEmailContentState] = React.useState<IRichContentEditorState | null>(null);
	const [recapEmailSubject, setRecapEmailSubject] = React.useState<string>('');
	const [recapEmailRecipients, setRecapEmailRecipients] = React.useState<string>('');
	const [isGeneratingRecapEmail, setIsGeneratingRecapEmail] = React.useState(false);
	const [isSendingRecapEmail, setIsSendingRecapEmail] = React.useState(false);

	// Mutations
	const transcriptsMutation = useAdminCalendarMeetingTranscriptsMutation();
	const summarizeMutation = useAdminTranscriptSummarizeMutation();
	const saveMeetingNoteMutation = useAdminAccountMeetingNoteMutation();
	const monitorComplaintsMutation = useAdminMonitorComplaintsMutation();
	const contentNotesGenerateMutation = useAdminContentNotesGenerateMutation();
	const contentNotesSaveMutation = useAdminContentNotesSaveMutation();
	const websiteUrlMutation = useAdminAccountWebsiteUrlMutation();

	// Account Profile mutations
	const accountProfileGenerateMutation = useAdminAccountProfileGenerateMutation();
	const accountProfileCreateMutation = useAccountProfileCreateMutation();
	const accountProfileUpdateMutation = useAccountProfileUpdateMutation();

	// Account Profile query - only query when we have a selected account
	const accountProfileImpersonationContext = React.useMemo(
		() => (selectedAccount?.id ? { account: { id: selectedAccount.id } } : undefined),
		[selectedAccount?.id]
	);
	const accountProfileQuery = useAccountProfileQuery({
		impersonationContext: accountProfileImpersonationContext,
	});

	// Branding query. The context carries the whole account (not just its id) so this shares a cache
	// entry with the BrandSettingsSection rendered inside the Brand Settings tab, which needs the
	// account's preferences to offer its "Apply existing logo" shortcut.
	const brandSettingsImpersonationContext = React.useMemo(
		() => (selectedAccount?.id ? { account: selectedAccount } : undefined),
		[selectedAccount]
	);
	const brandingQuery = useBrandingSettingsQuery({
		impersonationContext: brandSettingsImpersonationContext,
		enabled: !!selectedAccount?.id,
	});

	// Memoized CSM user lookup for Implementation team CC notice
	const csmUserForCcNotice = React.useMemo(() => {
		if (selectedTeamType !== 'ImplementationMeeting' || !selectedAccount?.additionalInfo?.customerSuccessId) {
			return null;
		}
		return activeUsers?.find(u => u.id === selectedAccount.additionalInfo?.customerSuccessId) ?? null;
	}, [selectedTeamType, selectedAccount?.additionalInfo?.customerSuccessId, activeUsers]);

	// Handle user selection
	const handleUserSelected = React.useCallback((user: IUser) => {
		if (user) {
			setSelectedUser(user);
			setSelectedMeeting(null);
			setSelectedMeetingWithTranscripts(null);
			setSelectedTranscript(null);
			setMeetingsData(null);
			setMeetingsError(null);
		}
	}, []);

	// Date navigation helpers
	const isToday = React.useMemo(() => {
		const today = new Date();
		return (
			selectedDate.getFullYear() === today.getFullYear() &&
			selectedDate.getMonth() === today.getMonth() &&
			selectedDate.getDate() === today.getDate()
		);
	}, [selectedDate]);

	const goToPreviousDay = React.useCallback(() => {
		const newDate = new Date(selectedDate);
		newDate.setDate(newDate.getDate() - 1);
		setSelectedDate(newDate);
	}, [selectedDate]);

	const goToNextDay = React.useCallback(() => {
		if (!isToday) {
			const newDate = new Date(selectedDate);
			newDate.setDate(newDate.getDate() + 1);
			setSelectedDate(newDate);
		}
	}, [selectedDate, isToday]);

	const handleDatePickerSelect = React.useCallback((day: Date) => {
		setSelectedDate(day);
		setIsDatePickerOpen(false);
	}, []);

	const formatDisplayDate = (date: Date) => {
		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	};

	// Fetch meetings for the selected date
	const fetchMeetings = React.useCallback(async () => {
		if (!selectedUser) {
			return;
		}

		// Reset state
		setSelectedMeeting(null);
		setSelectedMeetingWithTranscripts(null);
		setSelectedTranscript(null);
		setMeetingsError(null);

		setIsLoadingMeetings(true);

		try {
			// Convert to UTC for start and end of the selected day
			const startDateUtc = new Date(
				Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 0, 0, 0)
			);
			const endDateUtc = new Date(
				Date.UTC(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 23, 59, 59)
			);

			const url = `admin/calendar/meetings?userId=${encodeURIComponent(selectedUser.id)}&accountId=${encodeURIComponent(userSession.account.id)}&startDate=${startDateUtc.toISOString()}&endDate=${endDateUtc.toISOString()}`;

			const result = await userSession.webServiceHelper.callAsync<ICalendarMeetingsResponse>(url, 'GET');
			setMeetingsData(result);
		} catch (error) {
			logApiError('FetchMeetings-Error', error);
			setMeetingsError('Error loading meetings. Please try again or contact support.');
		} finally {
			setIsLoadingMeetings(false);
		}
	}, [selectedUser, selectedDate, userSession, logApiError]);

	// Auto-fetch when user or date changes
	React.useEffect(() => {
		if (selectedUser) {
			fetchMeetings();
		}
	}, [selectedUser, selectedDate, fetchMeetings]);

	// Handle find transcript
	const handleFindTranscript = React.useCallback(
		async (meeting: ICalendarMeetingInfo) => {
			if (!selectedUser || !meeting.startTime) {
				return;
			}

			setSelectedMeeting(meeting);
			setSelectedMeetingWithTranscripts(null);
			setSelectedTranscript(null);
			setSelectedTranscriptIndex(null);
			setExpandedTranscripts(new Set());
			setEditedTranscripts(new Map());
			setShowMeetingNoteForm(false);
			setIsTranscriptDialogOpen(true);

			try {
				const meetingDate = new Date(meeting.startTime);
				const startOfDay = new Date(meetingDate);
				startOfDay.setHours(0, 0, 0, 0);
				const endOfDay = new Date(meetingDate);
				endOfDay.setHours(23, 59, 59, 999);

				const result = await transcriptsMutation.mutateAsync({
					userId: selectedUser.id,
					accountId: userSession.account.id,
					userEmail: selectedUser.email,
					startDate: startOfDay,
					endDate: endOfDay,
				});

				// Find the matching meeting in the response
				const matchedMeeting = result.meetings?.find(
					m => m.meeting?.id === meeting.id || m.meeting?.iCalUid === meeting.iCalUid
				);

				setSelectedMeetingWithTranscripts(matchedMeeting || null);
			} catch (error) {
				logApiError('FindTranscript-Error', error);
				errorMessages?.pushApiError(error);
			}
		},
		[selectedUser, userSession.account.id, transcriptsMutation, logApiError, errorMessages]
	);

	// Toggle transcript expansion
	const toggleTranscriptExpansion = React.useCallback((index: number) => {
		setExpandedTranscripts(prev => {
			const newSet = new Set(prev);
			if (newSet.has(index)) {
				newSet.delete(index);
			} else {
				newSet.add(index);
			}
			return newSet;
		});
	}, []);

	// Handle transcript content edits
	const handleTranscriptContentChange = React.useCallback((index: number, content: string) => {
		setEditedTranscripts(prev => {
			const next = new Map(prev);
			next.set(index, content);
			return next;
		});
	}, []);

	// Get effective transcript content (edited or original)
	const getEffectiveTranscriptContent = React.useCallback(
		(index: number, match: ITranscriptMatch): string | undefined => {
			return editedTranscripts.has(index) ? editedTranscripts.get(index) : match.transcriptContent;
		},
		[editedTranscripts]
	);

	// Resolve account from meeting attendees
	const resolveAccountFromAttendees = React.useCallback(() => {
		// Reset prefill state first so a previously resolved account doesn't leak across meetings
		// when the current meeting has no matching attendee (e.g. user records a note for account A,
		// then records one for account B whose attendees don't expose a linkable accountId — without
		// this reset, the form would still show account A as the prefilled selection).
		setSelectedAccount(null);
		setAccountSearchValue('');
		setAccountStage(null);
		setAccountIdToFetch(null);

		const attendees = selectedMeetingWithTranscripts?.meeting?.attendees || [];
		// Look for an attendee with an accountId and companyName, excluding the organizer and Real Magic accounts
		const attendeeWithAccount = attendees.find(
			a => !a.isOrganizer && a.accountId && a.companyName && !RealMagicAccountIds.has(a.accountId)
		);
		if (attendeeWithAccount && attendeeWithAccount.accountId) {
			// Trigger fetch of full account data with features
			setAccountIdToFetch(attendeeWithAccount.accountId);
		}
	}, [selectedMeetingWithTranscripts]);

	// Handle record meeting note (replaces generate summary)
	const handleRecordMeetingNote = React.useCallback(
		async (transcript: ITranscriptMatch, index: number) => {
			const effectiveContent = getEffectiveTranscriptContent(index, transcript);
			if (!effectiveContent) {
				return;
			}

			// Store transcript with edited content
			const transcriptWithEdits: ITranscriptMatch = {
				...transcript,
				transcriptContent: effectiveContent,
			};
			setSelectedTranscript(transcriptWithEdits);
			setSelectedTranscriptIndex(index);

			try {
				const attendeeNames = selectedMeetingWithTranscripts?.meeting?.attendees?.map(getAttendeeDisplayName) || [];

				// Generate the summary
				const summaryResult = await summarizeMutation.mutateAsync({
					transcriptContent: effectiveContent,
					attendeeNames,
					meetingType: selectedTeamType,
					industry: selectedAccount?.additionalInfo?.industry,
				});

				// Pre-populate form fields
				const summaryHtml = markdownToEditorHtml(summaryResult?.summary || '');
				setMeetingNoteContentState(summaryHtml ? createContentStateWithHtmlStringValue(summaryHtml) : null);
				setDuration(selectedMeeting?.durationMinutes || transcript.durationMinutes || 60);
				setMeetingStatus('Completed');
				setValidationErrors([]);

				// Resolve account from attendees
				resolveAccountFromAttendees();

				// Show the form
				setShowMeetingNoteForm(true);
			} catch (error) {
				logApiError('RecordMeetingNote-Error', error);
				errorMessages?.pushApiError(error);
			}
		},
		[
			selectedMeetingWithTranscripts,
			selectedMeeting,
			summarizeMutation,
			logApiError,
			errorMessages,
			resolveAccountFromAttendees,
			selectedAccount?.additionalInfo?.industry,
			getEffectiveTranscriptContent,
			selectedTeamType,
		]
	);

	// Validate form before saving
	const validateForm = React.useCallback(() => {
		const errors: string[] = [];

		if (!selectedAccount) {
			errors.push('Please select an account.');
		}

		if (!meetingNoteContentState?.hasContent()) {
			errors.push('Please enter meeting notes.');
		}

		if (!meetingType) {
			errors.push('Please select a meeting type.');
		}

		setValidationErrors(errors);
		return errors.length === 0;
	}, [selectedAccount, meetingNoteContentState, meetingType]);

	// Handle save meeting note
	const handleSaveMeetingNote = React.useCallback(async () => {
		// Validate form
		if (!validateForm()) {
			return;
		}

		setIsSavingNote(true);

		try {
			// Prepare meeting tags
			const meetingTags: string[] = [];
			if (isOnboarding) {
				meetingTags.push('Onboarding');
			}

			// For Implementation team meetings, find the CSM user to CC on the note
			let ccUser: Api.IUser | undefined;
			if (selectedTeamType === 'ImplementationMeeting' && selectedAccount?.additionalInfo?.customerSuccessId) {
				ccUser = activeUsers?.find(u => u.id === selectedAccount.additionalInfo?.customerSuccessId);
			}

			// Extract HTML from editor state
			const rawContent = meetingNoteContentState?.getRawRichTextContent();
			const noteHtml = rawContent ? getContentHtmlStringValueFromRawRichTextContentState(rawContent) : '';

			// Link the note to the exact meeting it was recorded against. Prefer the transcript/calendar date;
			// for a manual note with no matched meeting, fall back to the date being browsed.
			// Fall back to the browsed date as a timezone-safe yyyy-MM-dd (toISOString would shift a day in UTC+ zones),
			// matching the manual-note path's format.
			const meetingDate =
				selectedTranscript?.meetingDate ||
				selectedMeeting?.startTime ||
				(selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : undefined);
			let meetingReference: IMeetingReference | undefined;
			if (selectedTranscript?.zoomRecordingId) {
				meetingReference = {
					source: MeetingReferenceSource.ZoomRecording,
					zoomRecordingId: selectedTranscript.zoomRecordingId,
					calendarMeetingId: selectedMeeting?.id,
					iCalUid: selectedMeeting?.iCalUid,
				};
			} else if (selectedMeeting?.id || selectedMeeting?.iCalUid) {
				meetingReference = {
					source: MeetingReferenceSource.CalendarEvent,
					calendarMeetingId: selectedMeeting?.id,
					iCalUid: selectedMeeting?.iCalUid,
				};
			}

			// Save the meeting note
			await saveMeetingNoteMutation.mutateAsync({
				account: selectedAccount,
				meetingType,
				meetingStatus,
				durationInMinutes: duration,
				meetingTags,
				noteHtml,
				ccUser,
				meetingDate,
				meetingReference,
			});

			// Show success message
			toast.success('Meeting note saved successfully!');

			// Best-effort: ask the platform to scan the transcript. The endpoint runs two independent
			// server-side analyses — client-complaint detection (Custom Content / Website / Ads), which
			// notifies the responsible departments, and a case-study (success-story) detector that emails
			// marketing. Both outputs are emailed server-side; nothing is rendered here.
			// Fire-and-forget — we do NOT await it, so the save flow proceeds immediately and the request
			// keeps running even if the rep closes the modal. Failures are logged only and never affect
			// the user-facing save.
			const complaintTranscriptContent =
				selectedTranscriptIndex != null && selectedTranscript
					? getEffectiveTranscriptContent(selectedTranscriptIndex, selectedTranscript)
					: undefined;
			if (
				selectedAccount?.id &&
				complaintTranscriptContent &&
				complaintTranscriptContent.length <= MONITOR_COMPLAINTS_MAX_TRANSCRIPT_CHARS
			) {
				monitorComplaintsMutation.mutate(
					{ accountId: selectedAccount.id, transcriptContent: complaintTranscriptContent },
					{ onError: error => logApiError('MonitorComplaints-Error', error as Api.IOperationResultNoValue) }
				);
			}

			// Update next meeting date on account if set
			if (nextMeetingDate && selectedAccount?.id) {
				try {
					await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${selectedAccount.id}/additionalInfo/nextMeeting?nextMeeting=${encodeURIComponent(moment(nextMeetingDate).format('MM/DD/YYYY'))}`,
						'PUT'
					);
				} catch (error) {
					// Log error but don't fail the whole operation since meeting note was saved
					logApiError('UpdateNextMeetingDate-Error', error);
					toast.error('Meeting note saved, but failed to update next meeting date');
				}
			}

			// Update account stage if set
			if (accountStage && selectedAccount?.id) {
				try {
					await userSession.webServiceHelper.callAsync<Api.IAccount>(
						`admin/account/${selectedAccount.id}/additionalInfo/stage?stage=${encodeURIComponent(accountStage)}`,
						'PUT'
					);
				} catch (error) {
					// Log error but don't fail the whole operation since meeting note was saved
					logApiError('UpdateAccountStage-Error', error);
					toast.error('Meeting note saved, but failed to update account stage');
				}
			}

			// Track that this meeting has notes recorded
			if (selectedMeeting?.id) {
				markMeetingLogged(selectedMeeting.id, selectedMeeting.startTime);
			}

			// Skip confirmation screens for Implementation meetings
			const isImplementationMeeting = selectedTeamType === 'ImplementationMeeting';

			// Check for enabled features (disabled for Implementation meetings)
			const isWebsiteAccount = !isImplementationMeeting && selectedAccount?.features?.websites?.enabled === true;
			const isContentCalendarAccount =
				!isImplementationMeeting && selectedAccount?.features?.customContentCalendar?.enabled === true;

			const needsAccountProfile = !isImplementationMeeting && !hasContentPreferences(accountProfileQuery.data);

			// A still-loading query is not evidence of blank branding, so wait for it to resolve.
			const needsBrandSettings =
				!isImplementationMeeting && brandingQuery.isSuccess && !hasBrandingSettings(brandingQuery.data);
			// Only worth offering when the industry actually has subverticals to choose from - otherwise
			// SubverticalSettingsContent renders no subvertical picker at all. It also reads
			// account.preferences unguarded, so require that rather than hand it an account it throws on.
			const needsSubverticalSettings =
				!isImplementationMeeting &&
				!!selectedAccount?.preferences &&
				industrySupportsSubverticals(selectedAccount.additionalInfo?.industry) &&
				!selectedAccount.preferences.subverticalSettings?.subverticals?.length;

			// Always show recap email option for Success Specialist meetings
			const shouldShowRecapEmail = !isImplementationMeeting;

			// Compute website URL once to avoid stale closure issues with state setters
			const additionalInfoUrl = selectedAccount?.additionalInfo?.websiteUrl;
			const featuresUrl = selectedAccount?.features?.websites?.websiteUrl;
			const emailDomain = selectedAccount?.emailDomain;
			const emailDomainUrl = emailDomain && emailDomain !== '@none.com' ? `https://${emailDomain}` : '';
			const computedWebsiteUrl = additionalInfoUrl || featuresUrl || emailDomainUrl;

			if (
				isWebsiteAccount ||
				isContentCalendarAccount ||
				needsAccountProfile ||
				needsBrandSettings ||
				needsSubverticalSettings ||
				shouldShowRecapEmail
			) {
				// Show confirmation screen(s) instead of closing
				setShowMeetingNoteForm(false);
				setDidSkipMeetingNote(false);

				// Reset tab state for new confirmation flow
				setCompletedTabs(new Set());

				// Determine first tab to show
				if (isWebsiteAccount) {
					setActiveConfirmationTab('webTeam');
				} else if (isContentCalendarAccount) {
					setActiveConfirmationTab('contentTeam');
				} else if (needsAccountProfile) {
					setActiveConfirmationTab('accountProfile');
				} else if (needsBrandSettings) {
					setActiveConfirmationTab('brandSettings');
				} else if (needsSubverticalSettings) {
					setActiveConfirmationTab('subverticalSettings');
				} else if (shouldShowRecapEmail) {
					setActiveConfirmationTab('recapEmail');
				}

				if (isWebsiteAccount) {
					setShowWebTeamConfirmation(true);

					// Pre-populate editable Deep Research fields
					setWebTeamBusinessType(selectedAccount?.additionalInfo?.industry || '');

					const address = selectedAccount?.planDetails?.address;
					const location = address?.city && address?.stateProvince ? `${address.city}, ${address.stateProvince}` : '';
					setWebTeamLocation(location);
					setWebTeamWebsiteUrl(computedWebsiteUrl);
				}

				if (isContentCalendarAccount) {
					setShowContentTeamConfirmation(true);
					setContentTeamWebsiteUrl(computedWebsiteUrl);
				}

				if (needsAccountProfile) {
					setShowAccountProfileConfirmation(true);
					setAccountProfileWebsiteUrl(computedWebsiteUrl);
				}

				if (needsBrandSettings) {
					setShowBrandSettingsConfirmation(true);
					setBrandSettingsWebsiteUrl(computedWebsiteUrl);
				}

				if (needsSubverticalSettings) {
					setShowSubverticalSettingsConfirmation(true);
				}

				if (shouldShowRecapEmail) {
					setShowRecapEmailConfirmation(true);
				}
				// Keep dialog open, don't reset state yet
			} else {
				// Close the form and reset state
				setShowMeetingNoteForm(false);
				setIsTranscriptDialogOpen(false);
				setSelectedAccount(null);
				setAccountSearchValue('');
				setAccountIdToFetch(null);
				setMeetingNoteContentState(null);
				setMeetingType(null);
				setMeetingStatus('Completed');
				setAccountStage(null);
				setDuration(DEFAULT_MEETING_DURATION_MINUTES);
				setIsOnboarding(false);
				setNextMeetingDate(null);
				setShowNextMeetingDatePicker(false);
				setValidationErrors([]);
			}
		} catch (error) {
			logApiError('SaveMeetingNote-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingNote(false);
		}
	}, [
		validateForm,
		selectedAccount,
		meetingType,
		meetingStatus,
		accountStage,
		duration,
		isOnboarding,
		meetingNoteContentState,
		saveMeetingNoteMutation,
		monitorComplaintsMutation,
		selectedTranscript,
		selectedTranscriptIndex,
		getEffectiveTranscriptContent,
		logApiError,
		errorMessages,
		selectedMeeting,
		selectedDate,
		nextMeetingDate,
		userSession,
		accountProfileQuery.data,
		brandingQuery.isSuccess,
		brandingQuery.data,
		selectedTeamType,
		activeUsers,
		markMeetingLogged,
	]);

	// Skip meeting note form and go directly to confirmation/post-meeting actions
	const handleSkipToConfirmation = React.useCallback(() => {
		if (!selectedAccount) {
			return;
		}

		// Clear validation errors since we're skipping
		setValidationErrors([]);
		setDidSkipMeetingNote(true);

		const isImplementationMeeting = selectedTeamType === 'ImplementationMeeting';

		const isWebsiteAccount = !isImplementationMeeting && selectedAccount?.features?.websites?.enabled === true;
		const isContentCalendarAccount =
			!isImplementationMeeting && selectedAccount?.features?.customContentCalendar?.enabled === true;
		const needsAccountProfile = !isImplementationMeeting && !hasContentPreferences(accountProfileQuery.data);
		// A still-loading query is not evidence of blank branding, so wait for it to resolve.
		const needsBrandSettings =
			!isImplementationMeeting && brandingQuery.isSuccess && !hasBrandingSettings(brandingQuery.data);
		// Only worth offering when the industry actually has subverticals to choose from - otherwise
		// SubverticalSettingsContent renders no subvertical picker at all. It also reads
		// account.preferences unguarded, so require that rather than hand it an account it throws on.
		const needsSubverticalSettings =
			!isImplementationMeeting &&
			!!selectedAccount?.preferences &&
			industrySupportsSubverticals(selectedAccount.additionalInfo?.industry) &&
			!selectedAccount.preferences.subverticalSettings?.subverticals?.length;
		const shouldShowRecapEmail = !isImplementationMeeting;

		// Compute website URL once to avoid stale closure issues with state setters
		const additionalInfoUrl = selectedAccount?.additionalInfo?.websiteUrl;
		const featuresUrl = selectedAccount?.features?.websites?.websiteUrl;
		const emailDomain = selectedAccount?.emailDomain;
		const emailDomainUrl = emailDomain && emailDomain !== '@none.com' ? `https://${emailDomain}` : '';
		const computedWebsiteUrl = additionalInfoUrl || featuresUrl || emailDomainUrl;

		if (
			isWebsiteAccount ||
			isContentCalendarAccount ||
			needsAccountProfile ||
			needsBrandSettings ||
			needsSubverticalSettings ||
			shouldShowRecapEmail
		) {
			setShowMeetingNoteForm(false);
			setCompletedTabs(new Set());

			if (isWebsiteAccount) {
				setActiveConfirmationTab('webTeam');
			} else if (isContentCalendarAccount) {
				setActiveConfirmationTab('contentTeam');
			} else if (needsAccountProfile) {
				setActiveConfirmationTab('accountProfile');
			} else if (needsBrandSettings) {
				setActiveConfirmationTab('brandSettings');
			} else if (needsSubverticalSettings) {
				setActiveConfirmationTab('subverticalSettings');
			} else if (shouldShowRecapEmail) {
				setActiveConfirmationTab('recapEmail');
			}

			if (isWebsiteAccount) {
				setShowWebTeamConfirmation(true);
				setWebTeamBusinessType(selectedAccount?.additionalInfo?.industry || '');

				const address = selectedAccount?.planDetails?.address;
				const location = address?.city && address?.stateProvince ? `${address.city}, ${address.stateProvince}` : '';
				setWebTeamLocation(location);
				setWebTeamWebsiteUrl(computedWebsiteUrl);
			}

			if (isContentCalendarAccount) {
				setShowContentTeamConfirmation(true);
				setContentTeamWebsiteUrl(computedWebsiteUrl);
			}

			if (needsAccountProfile) {
				setShowAccountProfileConfirmation(true);
				setAccountProfileWebsiteUrl(computedWebsiteUrl);
			}

			if (needsBrandSettings) {
				setShowBrandSettingsConfirmation(true);
				setBrandSettingsWebsiteUrl(computedWebsiteUrl);
			}

			if (needsSubverticalSettings) {
				setShowSubverticalSettingsConfirmation(true);
			}

			if (shouldShowRecapEmail) {
				setShowRecapEmailConfirmation(true);
			}
		} else {
			// No confirmation tabs to show — just close
			setShowMeetingNoteForm(false);
			setIsTranscriptDialogOpen(false);
			setSelectedAccount(null);
			setAccountSearchValue('');
			setAccountIdToFetch(null);
			setMeetingNoteContentState(null);
			setMeetingType(null);
			setMeetingStatus('Completed');
			setAccountStage(null);
			setDuration(DEFAULT_MEETING_DURATION_MINUTES);
			setIsOnboarding(false);
			setNextMeetingDate(null);
			setShowNextMeetingDatePicker(false);
			setValidationErrors([]);
			setDidSkipMeetingNote(false);
		}
	}, [selectedAccount, selectedTeamType, accountProfileQuery.data, brandingQuery.isSuccess, brandingQuery.data]);

	// Handle generating web team summary
	const handleGenerateWebTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) {
			return;
		}

		// Geographic scope is required — bail out if it hasn't been chosen (the UI also disables the button).
		if (!webTeamGeographicScope) {
			return;
		}

		setIsGeneratingWebTeamSummary(true);

		try {
			const attendeeNames = selectedMeetingWithTranscripts?.meeting?.attendees?.map(getAttendeeDisplayName) || [];

			// Generate the web team summary with Deep Research fields
			const summaryResult = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingForWebTeam',
				industry: selectedAccount?.additionalInfo?.industry,
				// Deep Research fields - use editable state values
				deepResearchAccountId: selectedAccount?.id,
				deepResearchBusinessName: selectedAccount?.companyName,
				deepResearchBusinessType: webTeamBusinessType || undefined,
				deepResearchLocation: webTeamLocation || undefined,
				deepResearchWebsiteUrl: webTeamWebsiteUrl || undefined,
				deepResearchGeographicScope: webTeamGeographicScope,
			});

			setWebTeamSummary(summaryResult?.summary || '');

			// Save website URL back to account if not already set
			if (!selectedAccount?.additionalInfo?.websiteUrl && webTeamWebsiteUrl && selectedAccount?.id) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: webTeamWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					// Log but don't fail the overall operation
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateWebTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingWebTeamSummary(false);
		}
	}, [
		selectedTranscript,
		selectedMeetingWithTranscripts,
		selectedAccount,
		summarizeMutation,
		webTeamBusinessType,
		webTeamLocation,
		webTeamWebsiteUrl,
		webTeamGeographicScope,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	// Helper to navigate to next incomplete tab or close the dialog
	const navigateToNextTabOrClose = React.useCallback(
		(justCompletedTab: ConfirmationTab) => {
			// Define tab order and their visibility flags
			const tabOrder: { tab: ConfirmationTab; isVisible: boolean }[] = [
				{ tab: 'webTeam', isVisible: showWebTeamConfirmation },
				{ tab: 'contentTeam', isVisible: showContentTeamConfirmation },
				{ tab: 'accountProfile', isVisible: showAccountProfileConfirmation },
				{ tab: 'brandSettings', isVisible: showBrandSettingsConfirmation },
				{ tab: 'subverticalSettings', isVisible: showSubverticalSettingsConfirmation },
				{ tab: 'recapEmail', isVisible: showRecapEmailConfirmation },
			];

			// Find next visible tab that isn't completed (including the one we just completed)
			const updatedCompletedTabs = new Set(completedTabs).add(justCompletedTab);
			const nextTab = tabOrder.find(t => t.isVisible && !updatedCompletedTabs.has(t.tab));

			if (nextTab) {
				// Navigate to the next incomplete tab
				setActiveConfirmationTab(nextTab.tab);
			} else {
				// All tabs complete - close the dialog
				setShowWebTeamConfirmation(false);
				setShowContentTeamConfirmation(false);
				setShowAccountProfileConfirmation(false);
				setShowBrandSettingsConfirmation(false);
				setShowSubverticalSettingsConfirmation(false);
				setShowRecapEmailConfirmation(false);
				setActiveConfirmationTab(null);
				setCompletedTabs(new Set());
				// Also close the transcript dialog
				setSelectedMeetingWithTranscripts(null);
			}
		},
		[
			showWebTeamConfirmation,
			showContentTeamConfirmation,
			showAccountProfileConfirmation,
			showBrandSettingsConfirmation,
			showSubverticalSettingsConfirmation,
			showRecapEmailConfirmation,
			completedTabs,
		]
	);

	/**
	 * Tab switching. Opening Brand Settings borrows a website URL the rep already typed on an earlier
	 * tab, so an account with no URL on file only has to be asked once.
	 */
	const handleConfirmationTabChange = React.useCallback(
		(tab: ConfirmationTab) => {
			if (tab === 'brandSettings') {
				setBrandSettingsWebsiteUrl(
					current => current || accountProfileWebsiteUrl || contentTeamWebsiteUrl || webTeamWebsiteUrl
				);
			}
			setActiveConfirmationTab(tab);
		},
		[accountProfileWebsiteUrl, contentTeamWebsiteUrl, webTeamWebsiteUrl]
	);

	// Brand Settings handlers
	const handleSaveBrandSettingsWebsiteUrl = React.useCallback(async () => {
		const websiteUrl = normalizeWebsiteUrl(brandSettingsWebsiteUrl);
		if (!selectedAccount?.id || !websiteUrl) return;

		setIsSavingBrandSettingsWebsiteUrl(true);
		try {
			await websiteUrlMutation.mutateAsync({ accountId: selectedAccount.id, websiteUrl });
			setBrandSettingsWebsiteUrlSaved(true);
			toast.success('Website URL saved to account');
		} catch (error) {
			logApiError('SaveWebsiteUrl-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingBrandSettingsWebsiteUrl(false);
		}
	}, [selectedAccount, brandSettingsWebsiteUrl, websiteUrlMutation, logApiError, errorMessages]);

	// Brand edits are saved by BrandSettingsSection itself, so Done only closes the tab out.
	const handleCompleteBrandSettings = React.useCallback(() => {
		setShowBrandSettingsConfirmation(false);
		setCompletedTabs(prev => new Set(prev).add('brandSettings'));
		navigateToNextTabOrClose('brandSettings');
	}, [navigateToNextTabOrClose]);

	// Likewise, SubverticalSettingsContent owns its own save.
	const handleCompleteSubverticalSettings = React.useCallback(() => {
		setShowSubverticalSettingsConfirmation(false);
		setCompletedTabs(prev => new Set(prev).add('subverticalSettings'));
		navigateToNextTabOrClose('subverticalSettings');
	}, [navigateToNextTabOrClose]);

	// Handle skipping web team summary
	const handleSkipWebTeamSummary = React.useCallback(() => {
		setShowWebTeamConfirmation(false);
		setWebTeamSummary(null);
		setWebTeamBusinessType('');
		setWebTeamLocation('');
		setWebTeamWebsiteUrl('');
		setWebTeamGeographicScope(null);
		setCompletedTabs(prev => new Set(prev).add('webTeam'));
	}, []);

	// Handle copying web team summary to clipboard
	const handleCopyWebTeamSummary = React.useCallback(() => {
		if (webTeamSummary) {
			navigator.clipboard.writeText(webTeamSummary);
			toast.success('Summary copied to clipboard!');
		}
	}, [webTeamSummary]);

	// Handle generating content team summary
	const handleGenerateContentTeamSummary = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent || !selectedAccount?.id || !contentTeamWebsiteUrl) {
			return;
		}

		setIsGeneratingContentTeamSummary(true);

		try {
			const generateResult = await contentNotesGenerateMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				accountId: selectedAccount.id,
				businessName: selectedAccount.companyName || '',
				websiteUrl: contentTeamWebsiteUrl,
				outputMix: contentTeamOutputMix ?? undefined,
				startMonth: contentTeamStartMonth,
				monthCount: contentTeamMonthCount,
				newsletterTemplateId: contentTeamNewsletterTemplateId.trim() || undefined,
			});

			// Response field is named `htmlContent` but actually contains markdown.
			const contentHtml = markdownToEditorHtml(generateResult?.htmlContent || '');
			setContentTeamContentState(contentHtml ? createContentStateWithHtmlStringValue(contentHtml) : null);
			setContentTeamSummary('generated'); // Use as flag to show editor view

			// Save website URL back to account if not already set
			if (!selectedAccount?.additionalInfo?.websiteUrl && contentTeamWebsiteUrl) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: contentTeamWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					// Log but don't fail the overall operation
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateContentTeamSummary-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingContentTeamSummary(false);
		}
	}, [
		selectedTranscript,
		selectedAccount,
		contentNotesGenerateMutation,
		contentTeamWebsiteUrl,
		contentTeamOutputMix,
		contentTeamStartMonth,
		contentTeamMonthCount,
		contentTeamNewsletterTemplateId,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	// Handle skipping content team summary
	const handleSkipContentTeamSummary = React.useCallback(() => {
		setShowContentTeamConfirmation(false);
		setContentTeamSummary(null);
		setContentTeamContentState(null);
		setContentTeamOutputMix(null);
		setContentTeamStartMonth(defaultStartMonth());
		setContentTeamMonthCount(DEFAULT_MONTH_COUNT);
		setContentTeamNewsletterTemplateId('');
		setContentTeamWebsiteUrl('');
		setCompletedTabs(prev => new Set(prev).add('contentTeam'));
	}, []);

	// Handle submitting content team note to ClickUp
	const handleSubmitContentTeamNote = React.useCallback(async () => {
		if (!contentTeamContentState || !selectedAccount?.id) {
			return;
		}

		setIsSubmittingContentTeamNote(true);

		try {
			const rawContent = contentTeamContentState.getRawRichTextContent();
			const htmlContent = getContentHtmlStringValueFromRawRichTextContentState(rawContent);

			await contentNotesSaveMutation.mutateAsync({
				htmlContent,
				accountId: selectedAccount.id,
				businessName: selectedAccount.companyName || '',
				industry: selectedAccount.additionalInfo?.industry || undefined,
			});

			toast.success('Content notes saved and sent to ClickUp!');

			// Reset content team state
			setShowContentTeamConfirmation(false);
			setContentTeamSummary(null);
			setContentTeamContentState(null);
			setContentTeamWebsiteUrl('');
		} catch (error) {
			logApiError('SubmitContentTeamNote-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSubmittingContentTeamNote(false);
		}
	}, [contentTeamContentState, selectedAccount, contentNotesSaveMutation, logApiError, errorMessages]);

	// Handle generating account profile (AI Custom Content preferences)
	const handleGenerateAccountProfile = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent || !selectedAccount?.id) {
			return;
		}

		setIsGeneratingAccountProfile(true);

		try {
			const result = await accountProfileGenerateMutation.mutateAsync({
				accountId: selectedAccount.id,
				transcriptContent: selectedTranscript.transcriptContent,
				websiteUrl: accountProfileWebsiteUrl || undefined,
			});

			setGeneratedBusinessDescription(result.businessDescription || '');
			setGeneratedKeywords(result.keywords || '');
			setGeneratedContentGoals(result.contentGoals || '');
			setGeneratedLocation(result.location || '');
			setGeneratedAddress(result.address || '');
			setGeneratedPhoneNumber(result.phoneNumber || '');
			setGeneratedPronouns(result.pronouns || '');
			setGeneratedTonePreference(result.tonePreference || '');

			// Save website URL back to account if not already set
			if (!selectedAccount?.additionalInfo?.websiteUrl && accountProfileWebsiteUrl) {
				try {
					await websiteUrlMutation.mutateAsync({
						accountId: selectedAccount.id,
						websiteUrl: accountProfileWebsiteUrl,
					});
					toast.success('Website URL saved to account');
				} catch (urlError) {
					// Log but don't fail the overall operation
					logApiError('SaveWebsiteUrl-Error', urlError);
				}
			}
		} catch (error) {
			logApiError('GenerateAccountProfile-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingAccountProfile(false);
		}
	}, [
		selectedTranscript,
		selectedAccount,
		accountProfileGenerateMutation,
		accountProfileWebsiteUrl,
		websiteUrlMutation,
		logApiError,
		errorMessages,
	]);

	// Handle saving account profile (AI Custom Content preferences)
	const handleSaveAccountProfile = React.useCallback(async () => {
		if (!selectedAccount?.id) {
			return;
		}

		setIsSavingAccountProfile(true);

		try {
			const profileData: Partial<Api.AccountProfile> = {
				businessDescription: generatedBusinessDescription,
				location: generatedLocation,
				address: generatedAddress,
				phoneNumber: generatedPhoneNumber,
				pronouns: generatedPronouns,
				tonePreference: generatedTonePreference,
				contentGoals: generatedContentGoals,
			};
			if (generatedKeywords) {
				profileData.messagingPreferences = { keywords: generatedKeywords };
			}

			// eslint-disable-next-line unicorn/prefer-ternary
			if (accountProfileQuery.data?.id) {
				// Update existing profile
				await accountProfileUpdateMutation.mutateAsync({
					accountProfile: {
						...accountProfileQuery.data,
						...profileData,
					},
					impersonationContext: accountProfileImpersonationContext,
				});
			} else {
				// Create new profile
				await accountProfileCreateMutation.mutateAsync({
					accountProfile: { id: null, ...profileData } as Api.AccountProfile,
					impersonationContext: accountProfileImpersonationContext,
				});
			}

			invalidateAccountProfileQueries({ impersonationContext: accountProfileImpersonationContext });
			toast.success('AI Custom Content preferences saved!');

			// Reset account profile state
			setShowAccountProfileConfirmation(false);
			setGeneratedBusinessDescription('');
			setGeneratedKeywords('');
			setGeneratedContentGoals('');
			setGeneratedLocation('');
			setGeneratedAddress('');
			setGeneratedPhoneNumber('');
			setGeneratedPronouns('');
			setGeneratedTonePreference('');
			setAccountProfileWebsiteUrl('');
		} catch (error) {
			logApiError('SaveAccountProfile-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingAccountProfile(false);
		}
	}, [
		selectedAccount,
		generatedBusinessDescription,
		generatedKeywords,
		generatedContentGoals,
		generatedLocation,
		generatedAddress,
		generatedPhoneNumber,
		generatedPronouns,
		generatedTonePreference,
		accountProfileQuery.data,
		accountProfileCreateMutation,
		accountProfileUpdateMutation,
		accountProfileImpersonationContext,
		logApiError,
		errorMessages,
	]);

	// Handle skipping account profile generation
	const handleSkipAccountProfile = React.useCallback(() => {
		setShowAccountProfileConfirmation(false);
		setGeneratedBusinessDescription('');
		setGeneratedKeywords('');
		setGeneratedContentGoals('');
		setGeneratedLocation('');
		setGeneratedAddress('');
		setGeneratedPhoneNumber('');
		setGeneratedPronouns('');
		setGeneratedTonePreference('');
		setAccountProfileWebsiteUrl('');
		setCompletedTabs(prev => new Set(prev).add('accountProfile'));
	}, []);

	// Handle generating recap email
	const handleGenerateRecapEmail = React.useCallback(async () => {
		if (!selectedTranscript?.transcriptContent) {
			return;
		}

		setIsGeneratingRecapEmail(true);

		try {
			const attendeeNames = selectedMeetingWithTranscripts?.meeting?.attendees?.map(getAttendeeDisplayName) || [];

			const result = await summarizeMutation.mutateAsync({
				transcriptContent: selectedTranscript.transcriptContent,
				attendeeNames,
				meetingType: 'SuccessClientMeetingRecapEmail',
				industry: selectedAccount?.additionalInfo?.industry,
			});

			// Convert plain text line breaks to HTML for rich text editor
			const summaryHtml = result?.summary ? result.summary.replace(/\n/g, '<br>') : null;
			setRecapEmailContentState(summaryHtml ? createContentStateWithHtmlStringValue(summaryHtml) : null);
			setRecapEmailSubject(`Follow-up: ${selectedMeetingWithTranscripts?.meeting?.summary || 'Our Recent Meeting'}`);

			// Pre-populate recipients from non-organizer attendees
			const attendeeEmails = (selectedMeetingWithTranscripts?.meeting?.attendees || [])
				.filter(a => !a.isOrganizer && a.email)
				.map(a => a.email)
				.join(', ');
			setRecapEmailRecipients(attendeeEmails);
		} catch (error) {
			logApiError('GenerateRecapEmail-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsGeneratingRecapEmail(false);
		}
	}, [
		selectedTranscript,
		selectedMeetingWithTranscripts,
		selectedAccount,
		summarizeMutation,
		logApiError,
		errorMessages,
	]);

	// Handle sending recap email
	const handleSendRecapEmail = React.useCallback(async () => {
		if (!recapEmailContentState?.hasContent()) {
			return;
		}

		if (!recapEmailRecipients.trim()) {
			toast.error('Please enter at least one recipient email address');
			return;
		}

		setIsSendingRecapEmail(true);

		try {
			// Extract HTML from rich content editor state
			const rawContent = recapEmailContentState.getRawRichTextContent();
			const emailBodyHtml = rawContent ? getContentHtmlStringValueFromRawRichTextContentState(rawContent) : '';

			// Parse recipients string into array (split by comma or semicolon, trim whitespace)
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const recipientsArray = recapEmailRecipients
				.split(/[,;]/)
				.map(email => email.trim())
				.filter(email => email.length > 0);

			// Validate all email addresses
			const invalidEmails = recipientsArray.filter(email => !emailRegex.test(email));
			if (invalidEmails.length > 0) {
				toast.error(`Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(', ')}`);
				setIsSendingRecapEmail(false);
				return;
			}

			// Send via the recap email endpoint
			const recipientCount = await userSession.webServiceHelper.callAsync<number>(
				'admin/userOperations/sendRecapEmail',
				'POST',
				{
					recipients: recipientsArray,
					subject: recapEmailSubject,
					emailBody: emailBodyHtml,
				}
			);
			if (recipientCount === 0) {
				toast.error('No emails were sent. Please check the recipient addresses.');
				return;
			}
			toast.success(`Recap email sent to ${recipientCount} recipient${recipientCount !== 1 ? 's' : ''}!`);
			// Clear recap email state
			setRecapEmailContentState(null);
			setRecapEmailSubject('');
			setRecapEmailRecipients('');
			// Mark as completed and navigate to next tab or close
			setCompletedTabs(prev => new Set(prev).add('recapEmail'));
			navigateToNextTabOrClose('recapEmail');
		} catch (error) {
			logApiError('SendRecapEmail-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSendingRecapEmail(false);
		}
	}, [
		recapEmailContentState,
		recapEmailSubject,
		recapEmailRecipients,
		userSession,
		logApiError,
		errorMessages,
		navigateToNextTabOrClose,
	]);

	// Handle skipping recap email
	const handleSkipRecapEmail = React.useCallback(() => {
		// Clear recap email state
		setRecapEmailContentState(null);
		setRecapEmailSubject('');
		setRecapEmailRecipients('');
		// Mark as completed and navigate to next tab or close
		setCompletedTabs(prev => new Set(prev).add('recapEmail'));
		navigateToNextTabOrClose('recapEmail');
	}, [navigateToNextTabOrClose]);

	// Handle opening the no show dialog
	const handleMarkNoShow = React.useCallback((meeting: ICalendarMeetingInfo) => {
		setNoShowMeeting(meeting);

		// Try to resolve account from attendees (excluding organizer)
		const attendees = meeting?.attendees || [];
		const attendeeWithAccount = attendees.find(
			a => !a.isOrganizer && a.accountId && a.companyName && !RealMagicAccountIds.has(a.accountId)
		);

		if (attendeeWithAccount) {
			const account: IAccount = {
				id: attendeeWithAccount.accountId,
				companyName: attendeeWithAccount.companyName,
			};
			setNoShowAccount(account);
			setNoShowAccountSearchValue(attendeeWithAccount.companyName);
		} else {
			setNoShowAccount(null);
			setNoShowAccountSearchValue('');
		}

		setIsNoShowDialogOpen(true);
	}, []);

	// Handle no show account search value change
	const handleNoShowAccountSearchValueChange = React.useCallback(
		(value: string) => {
			setNoShowAccountSearchValue(value);
			debouncedAccountSearchCallback(value);
		},
		[debouncedAccountSearchCallback]
	);

	// Handle saving the no show
	const handleSaveNoShow = React.useCallback(async () => {
		if (!noShowAccount || !noShowMeeting) {
			toast.error('Please select an account');
			return;
		}

		setIsSavingNoShow(true);

		try {
			// Save the no show with minimal data, linked to the meeting that was a no-show
			await saveMeetingNoteMutation.mutateAsync({
				account: noShowAccount,
				meetingType: CsmMeetingTypes.CheckIn, // Default to Check-in
				meetingStatus: 'No showed',
				durationInMinutes: noShowMeeting.durationMinutes || DEFAULT_NO_SHOW_DURATION_MINUTES,
				meetingTags: [],
				noteHtml: '<p>No show</p>',
				meetingDate: noShowMeeting.startTime,
				meetingReference:
					noShowMeeting.id || noShowMeeting.iCalUid
						? {
								source: MeetingReferenceSource.CalendarEvent,
								calendarMeetingId: noShowMeeting.id,
								iCalUid: noShowMeeting.iCalUid,
							}
						: undefined,
			});

			// Show success message
			toast.success('No show recorded successfully!');

			// Track that this meeting has been marked as no show
			if (noShowMeeting.id) {
				markMeetingNoShow(noShowMeeting.id, noShowMeeting.startTime);
			}

			// Close the dialog and reset state
			setIsNoShowDialogOpen(false);
			setNoShowMeeting(null);
			setNoShowAccount(null);
			setNoShowAccountSearchValue('');
		} catch (error) {
			logApiError('SaveNoShow-Error', error);
			errorMessages?.pushApiError(error);
		} finally {
			setIsSavingNoShow(false);
		}
	}, [noShowAccount, noShowMeeting, saveMeetingNoteMutation, logApiError, errorMessages, markMeetingNoShow]);

	// Format time for display in Eastern time (date is already shown in header)
	// API returns UTC times, so we need to treat them as UTC and convert to ET
	const formatTime = (dateString: string) => {
		if (!dateString) return 'N/A';
		const date = parseMeetingDate(dateString);
		return date.toLocaleTimeString('en-US', {
			hour: '2-digit',
			minute: '2-digit',
			timeZone: 'America/New_York',
		});
	};

	// Get match score style
	const getMatchScoreStyle = (score: number) => {
		if (score >= 70) return styleSheet.matchScoreHigh;
		if (score >= 50) return styleSheet.matchScoreMedium;
		return styleSheet.matchScoreLow;
	};

	// User search status
	let userSearchStatus: React.ReactNode = '';
	if (isLoadingUsers) {
		userSearchStatus = (
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2)}>
				<LoadingSpinner type='extra-tiny' />
				Searching Users...
			</div>
		);
	} else if (usersError) {
		userSearchStatus = 'Error loading users';
	} else if (activeUsers.length === 0 && userSearchValue) {
		userSearchStatus = `No users found for "${userSearchValue}"`;
	} else if (activeUsers.length > 0) {
		userSearchStatus = `${activeUsers.length} User${activeUsers.length === 1 ? '' : 's'} found`;
	} else if (!userSearchValue) {
		userSearchStatus = 'Start typing to search users...';
	}

	const meetingSchedule = selectedMeeting ? formatMeetingSchedule(selectedMeeting) : '';
	const externalAttendees = (selectedMeetingWithTranscripts?.meeting?.attendees || []).filter(isExternalAttendee);

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.section)}>
				<h2 className={css(styleSheet.sectionTitle)}>Log Meeting Notes</h2>
				<p>View calendar meetings for a user and match them with Zoom transcripts to generate AI summaries.</p>
			</div>

			{/* User Selection */}
			<div className={css(styleSheet.section)}>
				<div className={css(styleSheet.formGroup)}>
					<label htmlFor={`${id}-user-combobox`} className={css(styleSheet.label)}>
						Select User
					</label>
					<Combobox
						items={activeUsers}
						value={selectedUser}
						onValueChange={handleUserSelected}
						inputValue={userSearchValue}
						onInputValueChange={setUserSearchValue}
						itemToStringLabel={(user: IUser) => getDisplayName(user)}
						isItemEqualToValue={(item: IUser, value: IUser) => item.id === value.id}
					>
						<ComboboxInput placeholder='Search user...' id={`${id}-user-combobox`} />
						<ComboboxContent>
							<ComboboxStatus>{userSearchStatus}</ComboboxStatus>
							<ComboboxList>
								{(user: IUser) => (
									<ComboboxAvatarItem key={user.id} value={user}>
										<ComboboxAvatarItemAvatar
											imageUrl={user.profilePic}
											fallbackText={user.firstName?.substring(0, 1) + user.lastName?.substring(0, 1)}
										/>
										<ComboboxAvatarItemTitle>{getDisplayName(user)}</ComboboxAvatarItemTitle>
										<ComboboxAvatarItemDescription>{user.email}</ComboboxAvatarItemDescription>
									</ComboboxAvatarItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
					{selectedUser && (
						<div className={css(styleSheet.userInfo)}>
							✓ Selected: {getDisplayName(selectedUser)} ({selectedUser.email})
						</div>
					)}
				</div>

				{/* Date Navigation */}
				<div className={css(styleSheet.formGroup)}>
					<label className={css(styleSheet.label)}>Select Date</label>
					<div className={css(styleSheet.dateNavigationContainer)}>
						<button
							className={css(styleSheet.dateNavButton)}
							onClick={goToPreviousDay}
							disabled={isLoadingMeetings}
							aria-label='Previous day'
						>
							←
						</button>
						<Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
							<PopoverTrigger asChild>
								<button
									className={css(styleSheet.dateDisplay, styleSheet.dateDisplayClickable)}
									disabled={isLoadingMeetings}
									aria-label='Select date'
								>
									{formatDisplayDate(selectedDate)}
								</button>
							</PopoverTrigger>
							<PopoverContent side='bottom' align='center'>
								<DayPicker
									selectedDays={selectedDate}
									onDayClick={handleDatePickerSelect}
									maxDate={new Date()}
									allowPastDates
								/>
							</PopoverContent>
						</Popover>
						<button
							className={css(styleSheet.dateNavButton)}
							onClick={goToNextDay}
							disabled={isToday || isLoadingMeetings}
							aria-label='Next day'
						>
							→
						</button>
					</div>
					{isToday && <div className={css(styleSheet.todayIndicator)}>Today</div>}
				</div>
			</div>

			{/* Error Display */}
			{meetingsError && <div className={css(styleSheet.errorMessage)}>{meetingsError}</div>}

			{/* Loading State */}
			{isLoadingMeetings && (
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
				</div>
			)}

			{/* Meetings Table */}
			{meetingsData && !isLoadingMeetings && (
				<div className={css(styleSheet.section)}>
					<h3 className={css(styleSheet.sectionTitle)}>
						Meetings for {formatDisplayDate(selectedDate)} ({meetingsData.filteredCount})
					</h3>
					{meetingsData.meetings && meetingsData.meetings.length > 0 ? (
						<table className={css(styleSheet.meetingsTable)}>
							<thead className={css(styleSheet.tableHeader)}>
								<tr>
									<th className={css(styleSheet.tableHeaderCell, styleSheet.meetingDetailsHeader)}>Meeting Details</th>
									<th className={css(styleSheet.tableHeaderCell)}>Time</th>
									<th className={css(styleSheet.tableHeaderCell)}>Duration</th>
									<th className={css(styleSheet.tableHeaderCell)}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{meetingsData.meetings.map((meeting, index) => {
									const nonOrganizerAttendees = meeting.attendees?.filter(a => !a.isOrganizer) || [];
									const isNoShow = isMeetingNoShow(meeting.id, meeting.startTime);
									const isLogged = !isNoShow && isMeetingLogged(meeting.id, meeting.startTime);
									return (
										<tr key={meeting.id || index} className={css(styleSheet.tableRow)}>
											<td className={css(styleSheet.tableCell, styleSheet.meetingDetailsCell)}>
												<div className={css(styleSheet.meetingTitle)}>{meeting.summary || 'Untitled Meeting'}</div>
												{nonOrganizerAttendees.length > 0 && (
													<div className={css(styleSheet.attendeesPreview)}>
														<strong>Attendees: </strong>
														{nonOrganizerAttendees.map(getAttendeeDisplayName).join(', ')}
														{!isNoShow && (
															<button
																className={css(styleSheet.markNoShowButton)}
																onClick={e => {
																	e.stopPropagation();
																	handleMarkNoShow(meeting);
																}}
															>
																Mark No Show
															</button>
														)}
													</div>
												)}
											</td>
											<td className={css(styleSheet.tableCell)}>{formatTime(meeting.startTime)}</td>
											<td className={css(styleSheet.tableCell)}>{meeting.durationMinutes} min</td>
											<td className={css(styleSheet.tableCell)}>
												{isNoShow || isLogged ? (
													<div className={css(styleSheet.meetingStatusCell)}>
														<span
															className={css(isNoShow ? styleSheet.noShowIndicator : styleSheet.notesRecordedIndicator)}
														>
															{isNoShow ? 'No Show' : 'Notes Recorded'}
														</span>
														<Button
															variant='link'
															size='sm'
															styles={[styleSheet.logAnywayLink]}
															onClick={() => handleFindTranscript(meeting)}
														>
															Log meeting anyway
														</Button>
													</div>
												) : (
													<button
														className={css(styleSheet.actionButton)}
														onClick={() => handleFindTranscript(meeting)}
													>
														Find Transcript
													</button>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					) : (
						<div className={css(styleSheet.noDataMessage)}>
							No meetings found in the specified date range. Private meetings, all-day events, meetings over 3 hours,
							and solo meetings are filtered out.
						</div>
					)}
				</div>
			)}

			{/* Transcript Dialog */}
			<Dialog
				open={isTranscriptDialogOpen}
				onOpenChange={open => {
					setIsTranscriptDialogOpen(open);
					if (!open) {
						// Reset form state when dialog closes
						setShowMeetingNoteForm(false);
						setAccountIdToFetch(null);
						setEditedTranscripts(new Map());
						setSelectedTranscriptIndex(null);
						// Reset web team confirmation state
						setShowWebTeamConfirmation(false);
						setWebTeamSummary(null);
						setWebTeamGeographicScope(null);
						setIsGeneratingWebTeamSummary(false);
						// Reset content team confirmation state
						setShowContentTeamConfirmation(false);
						setContentTeamSummary(null);
						setContentTeamContentState(null);
						setContentTeamOutputMix(null);
						setContentTeamStartMonth(defaultStartMonth());
						setContentTeamMonthCount(DEFAULT_MONTH_COUNT);
						setContentTeamNewsletterTemplateId('');
						// Reset account profile confirmation state
						setShowAccountProfileConfirmation(false);
						setGeneratedBusinessDescription('');
						setGeneratedKeywords('');
						setGeneratedContentGoals('');
						// Reset brand settings confirmation state
						setShowBrandSettingsConfirmation(false);
						setBrandSettingsWebsiteUrl('');
						setBrandSettingsWebsiteUrlSaved(false);
						// Reset industry & subvertical confirmation state
						setShowSubverticalSettingsConfirmation(false);
						// Reset recap email confirmation state
						setShowRecapEmailConfirmation(false);
						setRecapEmailContentState(null);
						setRecapEmailSubject('');
						setRecapEmailRecipients('');
						// Reset tab state
						setActiveConfirmationTab(null);
						setCompletedTabs(new Set());
					}
				}}
			>
				<DialogPopup
					styles={[
						showContentTeamConfirmation ||
						showAccountProfileConfirmation ||
						showBrandSettingsConfirmation ||
						showSubverticalSettingsConfirmation ||
						showRecapEmailConfirmation ||
						showWebTeamConfirmation
							? styleSheet.transcriptDialogConfirmation
							: showMeetingNoteForm
								? styleSheet.transcriptDialogWide
								: styleSheet.transcriptDialog,
					]}
				>
					<DialogHeader>
						<DialogTitle>
							{showWebTeamConfirmation ||
							showContentTeamConfirmation ||
							showAccountProfileConfirmation ||
							showBrandSettingsConfirmation ||
							showSubverticalSettingsConfirmation ||
							showRecapEmailConfirmation
								? 'Post-Meeting Actions'
								: showMeetingNoteForm
									? 'Record Meeting Note'
									: `Transcript Matches: ${selectedMeeting?.summary || 'Meeting'}`}
						</DialogTitle>
						{showMeetingNoteForm && selectedMeeting && (
							<div className={css(styleSheet.meetingContext)}>
								{selectedMeeting.summary && (
									<div className={css(styleSheet.meetingContextName)}>{selectedMeeting.summary}</div>
								)}
								{meetingSchedule && <div className={css(styleSheet.meetingContextTime)}>{meetingSchedule}</div>}
							</div>
						)}
					</DialogHeader>
					<DialogBody styles={[styleSheet.transcriptDialogBody]}>
						{transcriptsMutation.isPending && (
							<div className={css(styleSheet.loadingContainer)}>
								<LoadingSpinner type='large' />
								<span className={css(styleSheet.loadingText)}>Searching for matching Zoom transcripts...</span>
							</div>
						)}

						{!transcriptsMutation.isPending &&
							selectedMeetingWithTranscripts &&
							!showWebTeamConfirmation &&
							!showContentTeamConfirmation &&
							!showAccountProfileConfirmation &&
							!showBrandSettingsConfirmation &&
							!showSubverticalSettingsConfirmation &&
							!showRecapEmailConfirmation && (
								<>
									{/* Show warning banner for partial failures when we have results */}
									{selectedMeetingWithTranscripts.hasPartialFailures &&
										selectedMeetingWithTranscripts.transcriptMatches &&
										selectedMeetingWithTranscripts.transcriptMatches.length > 0 && (
											<div className={css(styleSheet.warningBanner)}>
												<span className={css(styleSheet.warningIcon)}>&#9888;</span>
												<span>
													{selectedMeetingWithTranscripts.transcriptLoadingStatus ||
														`${selectedMeetingWithTranscripts.failedTranscriptDownloads} transcript(s) could not be loaded. Some meetings may be missing from results.`}
												</span>
											</div>
										)}

									{/* Show API error if present */}
									{selectedMeetingWithTranscripts.matchError && (
										<div className={css(styleSheet.errorBanner)}>
											<span className={css(styleSheet.errorIcon)}>&#10060;</span>
											<span className={css(styleSheet.errorText)}>{selectedMeetingWithTranscripts.matchError}</span>
											<button
												className={css(styleSheet.retryButton)}
												onClick={() => selectedMeeting && handleFindTranscript(selectedMeeting)}
											>
												Retry
											</button>
										</div>
									)}

									{/* Show all-failed error when no results but failures occurred */}
									{!selectedMeetingWithTranscripts.matchError &&
										(!selectedMeetingWithTranscripts.transcriptMatches ||
											selectedMeetingWithTranscripts.transcriptMatches.length === 0) &&
										selectedMeetingWithTranscripts.failedTranscriptDownloads &&
										selectedMeetingWithTranscripts.failedTranscriptDownloads > 0 && (
											<div className={css(styleSheet.errorBanner)}>
												<span className={css(styleSheet.errorIcon)}>&#10060;</span>
												<span className={css(styleSheet.errorText)}>
													Unable to load Zoom transcripts. Please try again.
												</span>
												<button
													className={css(styleSheet.retryButton)}
													onClick={() => selectedMeeting && handleFindTranscript(selectedMeeting)}
												>
													Retry
												</button>
											</div>
										)}

									{selectedMeetingWithTranscripts.transcriptMatches &&
									selectedMeetingWithTranscripts.transcriptMatches.length > 0 ? (
										<div>
											{/* Team Type Selector - only show when not in form view */}
											{!showMeetingNoteForm && (
												<div className={css(styleSheet.teamTypeSelector)}>
													<label className={css(styleSheet.teamTypeSelectorLabel)}>Role:</label>
													<Select
														value={selectedTeamType}
														onValueChange={(value: string) => setSelectedTeamType(value as TeamType)}
													>
														<SelectTrigger styles={styleSheet.teamTypeTrigger}>
															{TEAM_TYPE_OPTIONS.find(opt => opt.value === selectedTeamType)?.label}
														</SelectTrigger>
														<SelectContent>
															{TEAM_TYPE_OPTIONS.map(option => (
																<SelectItem key={option.value} value={option.value}>
																	{option.label}
																</SelectItem>
															))}
														</SelectContent>
													</Select>
												</div>
											)}
											{showMeetingNoteForm && selectedTranscript ? (
												// When form is shown, render it without the card wrapper
												<>
													<div className={css(styleSheet.meetingNoteForm)}>
														{/* Validation Errors */}
														{validationErrors.length > 0 && (
															<div className={css(styleSheet.validationErrors)}>
																{validationErrors.map((error, idx) => (
																	<div key={idx} className={css(styleSheet.validationError)}>
																		{error}
																	</div>
																))}
															</div>
														)}

														{/* Two Column Layout */}
														<div className={css(styleSheet.formColumns)}>
															{/* Left Column */}
															<div className={css(styleSheet.formLeftColumn)}>
																{/* Account Selection/Display */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>
																		Account <span className={css(styleSheet.required)}>*</span>
																	</label>
																	{selectedAccount ? (
																		<div className={css(styleSheet.selectedAccountDisplay)}>
																			<span>{selectedAccount.companyName}</span>
																			<button
																				className={css(styleSheet.changeAccountButton)}
																				onClick={() => {
																					setSelectedAccount(null);
																					setAccountSearchValue('');
																					setAccountStage(null);
																				}}
																			>
																				Change
																			</button>
																		</div>
																	) : (
																		<Combobox
																			items={accounts}
																			value={selectedAccount}
																			onValueChange={(account: IAccount | null) => {
																				setSelectedAccount(account);
																				setAccountStage(account?.additionalInfo?.stage || null);
																			}}
																			inputValue={accountSearchValue}
																			onInputValueChange={handleAccountSearchValueChange}
																			itemToStringLabel={(account: IAccount) => account.companyName || ''}
																			isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
																		>
																			<ComboboxInput placeholder='Search for account...' />
																			<ComboboxContent>
																				<ComboboxStatus>
																					{accountsQuery.isLoading
																						? 'Searching...'
																						: accounts.length === 0 && accountSearchValue
																							? `No accounts found for "${accountSearchValue}"`
																							: accounts.length > 0
																								? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
																								: 'Start typing to search accounts...'}
																				</ComboboxStatus>
																				<ComboboxList>
																					{(account: IAccount) => (
																						<ComboboxAvatarItem key={account.id} value={account}>
																							<ComboboxAvatarItemAvatar
																								imageUrl={''}
																								fallbackText={account.companyName?.substring(0, 2).toUpperCase()}
																							/>
																							<ComboboxAvatarItemTitle>{account.companyName}</ComboboxAvatarItemTitle>
																						</ComboboxAvatarItem>
																					)}
																				</ComboboxList>
																			</ComboboxContent>
																		</Combobox>
																	)}
																</div>

																{/* Attendees, with the account each one resolved to */}
																{externalAttendees.length > 0 && (
																	<div className={css(styleSheet.formField)}>
																		<div className={css(styleSheet.formLabel)}>Attendees</div>
																		<div className={css(styleSheet.attendeeList)}>
																			{externalAttendees.map((attendee, index) => {
																				const displayName = getAttendeeDisplayName(attendee);
																				return (
																					<div
																						className={css(styleSheet.attendeeRow)}
																						key={attendee.userId || attendee.email || `attendee-${index}`}
																					>
																						<Avatar
																							altText={displayName}
																							fallbackStyles={[styleSheet.attendeeAvatarFallback]}
																							fallbackText={getInitials(displayName, attendee.email)}
																							smallText={true}
																							styles={[styleSheet.attendeeAvatar]}
																						/>
																						<span className={css(styleSheet.attendeeName)}>{displayName}</span>
																						{attendee.companyName && (
																							<span className={css(styleSheet.attendeeCompany)}>
																								{attendee.companyName}
																							</span>
																						)}
																					</div>
																				);
																			})}
																		</div>
																	</div>
																)}

																{/* Meeting Note Content */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>
																		Meeting Notes <span className={css(styleSheet.required)}>*</span>
																	</label>
																	<div className={`${css(styleSheet.richEditorWrapper)} ${MEETING_NOTE_EDITOR_CLASS}`}>
																		<RichContentDocumentEditor
																			contentState={meetingNoteContentState}
																			onContentStateChanged={setMeetingNoteContentState}
																			readOnly={isSavingNote}
																			config={{
																				autoresizeToFitContent: true,
																				minHeight: 300,
																				contentHorizontalPadding: 20,
																				toolbar: 'undo redo | bold italic underline | bullist numlist',
																				contentRawCss: ContentTeamEditorCSS,
																			}}
																		/>
																	</div>
																</div>
															</div>

															{/* Right Column */}
															<div className={css(styleSheet.formRightColumn)}>
																{/* Meeting Type */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>
																		Meeting Type <span className={css(styleSheet.required)}>*</span>
																	</label>
																	<Combobox
																		items={Object.values(CsmMeetingTypes)}
																		value={meetingType}
																		onValueChange={value => setMeetingType(value as CsmMeetingTypes)}
																	>
																		<ComboboxInput placeholder='Search meeting type...' showClear />
																		<ComboboxContent>
																			<ComboboxEmpty>No meeting types found.</ComboboxEmpty>
																			<ComboboxList>
																				{(type: string) => (
																					<ComboboxItem key={type} value={type}>
																						<ComboboxItemRadioIndicator />
																						{type}
																					</ComboboxItem>
																				)}
																			</ComboboxList>
																		</ComboboxContent>
																	</Combobox>
																</div>

																{/* Onboarding Checkbox */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.checkboxLabel)}>
																		<input
																			type='checkbox'
																			checked={isOnboarding}
																			onChange={e => setIsOnboarding(e.target.checked)}
																		/>
																		<span className={css(styleSheet.onboardingText)}>
																			This was part of the onboarding process
																		</span>
																	</label>
																</div>

																{/* Meeting Status */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>Meeting Status</label>
																	<Select value={meetingStatus} onValueChange={setMeetingStatus}>
																		<SelectTrigger>
																			<SelectValue />
																		</SelectTrigger>
																		<SelectContent>
																			<SelectItem value='Completed'>Completed</SelectItem>
																			<SelectItem value='No showed'>No showed</SelectItem>
																			<SelectItem value='Rescheduled'>Rescheduled</SelectItem>
																		</SelectContent>
																	</Select>
																</div>

																{/* Account Stage */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>Account Stage</label>
																	<Combobox
																		items={NormalizedStages}
																		value={accountStage}
																		onValueChange={value => setAccountStage(value as string | null)}
																	>
																		<ComboboxInput placeholder='Select account stage...' showClear />
																		<ComboboxContent>
																			<ComboboxEmpty>No stages found.</ComboboxEmpty>
																			<ComboboxList>
																				{(stage: string) => (
																					<ComboboxItem key={stage} value={stage}>
																						<ComboboxItemRadioIndicator />
																						{stage}
																					</ComboboxItem>
																				)}
																			</ComboboxList>
																		</ComboboxContent>
																	</Combobox>
																</div>

																{/* Duration */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>Duration (minutes)</label>
																	<Select value={duration} onValueChange={setDuration}>
																		<SelectTrigger>
																			<SelectValue />
																		</SelectTrigger>
																		<SelectContent>
																			{[15, 30, 45, 60, 90, 120].map(mins => (
																				<SelectItem key={mins} value={mins}>
																					{mins} minutes
																				</SelectItem>
																			))}
																		</SelectContent>
																	</Select>
																</div>

																{/* Next Meeting Date */}
																<div className={css(styleSheet.formField)}>
																	<label className={css(styleSheet.formLabel)}>Next Meeting Date</label>
																	<div className={css(styleSheet.nextMeetingContainer)}>
																		<button
																			type='button'
																			className={css(styleSheet.nextMeetingButton)}
																			onClick={() => setShowNextMeetingDatePicker(!showNextMeetingDatePicker)}
																		>
																			{nextMeetingDate
																				? nextMeetingDate.toLocaleDateString('en-US', {
																						weekday: 'short',
																						year: 'numeric',
																						month: 'short',
																						day: 'numeric',
																					})
																				: 'Select date...'}
																		</button>
																		{nextMeetingDate && (
																			<button
																				type='button'
																				className={css(styleSheet.clearDateButton)}
																				onClick={() => setNextMeetingDate(null)}
																			>
																				Clear
																			</button>
																		)}
																	</div>
																	{showNextMeetingDatePicker && (
																		<div className={css(styleSheet.datePickerContainer)}>
																			<DayPicker
																				allowPastDates={false}
																				onDayClick={day => {
																					setNextMeetingDate(day);
																					setShowNextMeetingDatePicker(false);
																				}}
																				selectedDays={nextMeetingDate || undefined}
																			/>
																		</div>
																	)}
																</div>

																{/* Action Buttons */}
																<div className={css(styleSheet.formActions)}>
																	<button
																		className={css(styleSheet.cancelButton)}
																		onClick={handleSkipToConfirmation}
																		disabled={isSavingNote || !selectedAccount}
																	>
																		Skip
																	</button>
																	<button
																		className={css(styleSheet.saveButton)}
																		onClick={handleSaveMeetingNote}
																		disabled={isSavingNote}
																	>
																		{isSavingNote ? 'Saving...' : 'Save Meeting Note'}
																	</button>
																</div>
																{/* Show CC notice for Implementation team meetings */}
																{csmUserForCcNotice && (
																	<div className={css(styleSheet.ccNotice)}>
																		We&apos;ll automatically cc {getDisplayName(csmUserForCcNotice)}
																	</div>
																)}
															</div>
														</div>
													</div>
												</>
											) : (
												// When form is not shown, render transcript cards normally
												selectedMeetingWithTranscripts.transcriptMatches.map((match, index) => {
													const isExpanded = expandedTranscripts.has(index);
													const isGeneratingSummary = summarizeMutation.isPending && selectedTranscriptIndex === index;
													return (
														<div key={index} className={css(styleSheet.transcriptCard)}>
															<div className={css(styleSheet.transcriptCardHeader)}>
																<div className={css(styleSheet.fullWidth)}>
																	<div className={css(styleSheet.transcriptTopic)}>
																		{match.meetingTopic || 'Untitled Meeting'}
																	</div>
																	<div className={css(styleSheet.transcriptMeta)}>
																		<strong>Duration:</strong> {match.durationMinutes} minutes
																		<span className={css(styleSheet.transcriptDivider)}>•</span>
																		<strong>Match Score:</strong>{' '}
																		<span className={css(styleSheet.matchScore, getMatchScoreStyle(match.matchScore))}>
																			{Math.round(match.matchScore)}%
																		</span>
																	</div>
																	{match.matchReasons && match.matchReasons.length > 0 && (
																		<div className={css(styleSheet.transcriptReasons)}>
																			{match.matchReasons.join(', ')}
																		</div>
																	)}
																</div>
																<button
																	className={css(styleSheet.transcriptLink)}
																	onClick={() => handleRecordMeetingNote(match, index)}
																	disabled={isGeneratingSummary}
																>
																	{isGeneratingSummary ? 'Preparing Note...' : 'Record Meeting Note'}
																</button>
															</div>

															{isExpanded && match.transcriptContent && (
																<div className={css(styleSheet.expandedContent)}>
																	<textarea
																		className={css(styleSheet.transcriptContentEditable)}
																		value={getEffectiveTranscriptContent(index, match) || ''}
																		onChange={e => handleTranscriptContentChange(index, e.target.value)}
																		rows={15}
																	/>
																</div>
															)}

															{(match.transcriptContent || match.playUrl) && (
																<div className={css(styleSheet.transcriptCardFooter)}>
																	{match.transcriptContent && (
																		<button
																			className={css(styleSheet.viewTranscriptLink)}
																			onClick={() => toggleTranscriptExpansion(index)}
																		>
																			{isExpanded ? 'Hide Transcript' : 'View Full Transcript'}
																		</button>
																	)}
																	{match.transcriptContent && match.playUrl && (
																		<span className={css(styleSheet.pipeDivider)}>|</span>
																	)}
																	{match.playUrl && (
																		<button
																			className={css(styleSheet.viewTranscriptLink)}
																			onClick={() => window.open(match.playUrl, '_blank')}
																		>
																			Play Recording
																		</button>
																	)}
																	{match.transcriptContent && (
																		<>
																			<span className={css(styleSheet.pipeDivider)}>|</span>
																			<button
																				className={css(styleSheet.viewTranscriptLink)}
																				onClick={() => setAskLevTranscript(match)}
																			>
																				Ask Lev
																			</button>
																		</>
																	)}
																</div>
															)}
														</div>
													);
												})
											)}
										</div>
									) : (
										// Only show "no transcripts" message if there are no errors
										!selectedMeetingWithTranscripts.matchError &&
										!selectedMeetingWithTranscripts.failedTranscriptDownloads && (
											<div className={css(styleSheet.noDataMessage)}>
												No matching Zoom transcripts found for this meeting.
											</div>
										)
									)}
								</>
							)}

						{/* Tabbed Confirmation Interface */}
						{(showWebTeamConfirmation ||
							showContentTeamConfirmation ||
							showAccountProfileConfirmation ||
							showBrandSettingsConfirmation ||
							showSubverticalSettingsConfirmation ||
							showRecapEmailConfirmation) && (
							<div className={css(styleSheet.confirmationContainer)}>
								{/* Success message header */}
								<div className={css(styleSheet.confirmationMessage)}>
									<div className={css(styleSheet.confirmationTitle)}>
										{didSkipMeetingNote ? 'Post-Meeting Actions' : 'Meeting Note Saved!'}
									</div>
									<div className={css(styleSheet.confirmationText)}>
										Complete any of the optional tasks below, or close the dialog when done.
									</div>
								</div>

								{/* The account the note was filed against — read-only, since changing it here
								    would re-target the optional tasks while leaving the saved note misfiled. */}
								{selectedAccount && (
									<div className={css(styleSheet.formField)}>
										<div className={css(styleSheet.formLabel)}>Account</div>
										<div className={css(styleSheet.selectedAccountDisplay)}>
											<span>{selectedAccount.companyName}</span>
										</div>
									</div>
								)}

								{/* Tab Bar */}
								<div className={css(styleSheet.confirmationTabBar)}>
									{showWebTeamConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'webTeam' && styleSheet.confirmationTabActive,
												completedTabs.has('webTeam') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('webTeam')}
										>
											Web Team {completedTabs.has('webTeam') && '✓'}
										</button>
									)}
									{showContentTeamConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'contentTeam' && styleSheet.confirmationTabActive,
												completedTabs.has('contentTeam') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('contentTeam')}
										>
											Content Team {completedTabs.has('contentTeam') && '✓'}
										</button>
									)}
									{showAccountProfileConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'accountProfile' && styleSheet.confirmationTabActive,
												completedTabs.has('accountProfile') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('accountProfile')}
										>
											AI Custom Content {completedTabs.has('accountProfile') && '✓'}
										</button>
									)}
									{showBrandSettingsConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'brandSettings' && styleSheet.confirmationTabActive,
												completedTabs.has('brandSettings') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('brandSettings')}
										>
											Brand Settings {completedTabs.has('brandSettings') && '✓'}
										</button>
									)}
									{showSubverticalSettingsConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'subverticalSettings' && styleSheet.confirmationTabActive,
												completedTabs.has('subverticalSettings') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('subverticalSettings')}
										>
											Industry & Subvertical {completedTabs.has('subverticalSettings') && '✓'}
										</button>
									)}
									{showRecapEmailConfirmation && (
										<button
											className={css(
												styleSheet.confirmationTab,
												activeConfirmationTab === 'recapEmail' && styleSheet.confirmationTabActive,
												completedTabs.has('recapEmail') && styleSheet.confirmationTabCompleted
											)}
											onClick={() => handleConfirmationTabChange('recapEmail')}
										>
											Recap Email {completedTabs.has('recapEmail') && '✓'}
										</button>
									)}
								</div>

								{/* Tab Content - Full width for each panel */}
								<div className={css(styleSheet.confirmationTabContent)}>
									{/* Web Team Tab */}
									{activeConfirmationTab === 'webTeam' && (
										<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
											{!webTeamSummary ? (
												<div className={css(styleSheet.confirmationPanel)}>
													<h3 className={css(styleSheet.confirmationPanelTitle)}>Web Team Intake</h3>

													<div className={css(styleSheet.deepResearchFields)}>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Business Type</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamBusinessType}
																onChange={e => setWebTeamBusinessType(e.target.value)}
																placeholder='e.g., Insurance Agency'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Location</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamLocation}
																onChange={e => setWebTeamLocation(e.target.value)}
																placeholder='e.g., Raleigh, NC'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={webTeamWebsiteUrl}
																onChange={e => setWebTeamWebsiteUrl(e.target.value)}
																placeholder='e.g., https://example.com'
																disabled={isGeneratingWebTeamSummary}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>
																Geographic Scope <span className={css(styleSheet.deepResearchRequired)}>*</span>
															</label>
															<Select
																value={webTeamGeographicScope ?? ''}
																onValueChange={(value: string) =>
																	setWebTeamGeographicScope(
																		GEOGRAPHIC_SCOPE_OPTIONS.some(opt => opt.value === value)
																			? (value as GeographicScope)
																			: null
																	)
																}
																disabled={isGeneratingWebTeamSummary}
															>
																<SelectTrigger styles={styleSheet.deepResearchScopeTrigger}>
																	{GEOGRAPHIC_SCOPE_OPTIONS.find(opt => opt.value === webTeamGeographicScope)
																		?.label ?? (
																		<span className={css(styleSheet.deepResearchScopePlaceholder)}>Select scope…</span>
																	)}
																</SelectTrigger>
																<SelectContent>
																	{GEOGRAPHIC_SCOPE_OPTIONS.map(option => (
																		<SelectItem key={option.value} value={option.value}>
																			<div className={css(styleSheet.deepResearchScopeOption)}>
																				<span className={css(styleSheet.deepResearchScopeOptionLabel)}>
																					{option.label}
																				</span>
																				<span className={css(styleSheet.deepResearchScopeOptionDescription)}>
																					{option.description}
																				</span>
																			</div>
																		</SelectItem>
																	))}
																</SelectContent>
															</Select>
														</div>
													</div>

													<div className={css(styleSheet.confirmationActions)}>
														<Button
															variant='secondary'
															onClick={handleSkipWebTeamSummary}
															disabled={isGeneratingWebTeamSummary}
														>
															Skip
														</Button>
														<Button
															variant='success'
															onClick={handleGenerateWebTeamSummary}
															isLoading={isGeneratingWebTeamSummary}
															disabled={isGeneratingWebTeamSummary || !webTeamGeographicScope}
														>
															Generate Web Team Summary
														</Button>
													</div>
												</div>
											) : (
												<>
													<div className={css(styleSheet.summaryHeader)}>
														<h3>Web Team Intake Summary</h3>
														<Button variant='primary' onClick={handleCopyWebTeamSummary}>
															Copy
														</Button>
													</div>
													<div className={css(styleSheet.summaryNote)}>
														{
															"We'll automatically save this note to the account along with a research report for the web team."
														}
													</div>
													<div className={css(styleSheet.summaryContent)}>{webTeamSummary}</div>
												</>
											)}
										</div>
									)}

									{/* Content Team Tab */}
									{activeConfirmationTab === 'contentTeam' && (
										<ContentTeamTab
											websiteUrl={contentTeamWebsiteUrl}
											onWebsiteUrlChange={setContentTeamWebsiteUrl}
											outputMix={contentTeamOutputMix}
											onOutputMixChange={setContentTeamOutputMix}
											startMonth={contentTeamStartMonth}
											onStartMonthChange={setContentTeamStartMonth}
											monthCount={contentTeamMonthCount}
											onMonthCountChange={setContentTeamMonthCount}
											newsletterTemplateId={contentTeamNewsletterTemplateId}
											onNewsletterTemplateIdChange={setContentTeamNewsletterTemplateId}
											hasGeneratedSummary={!!contentTeamSummary}
											contentState={contentTeamContentState}
											onContentStateChange={setContentTeamContentState}
											isGenerating={isGeneratingContentTeamSummary}
											isSubmitting={isSubmittingContentTeamNote}
											onGenerate={handleGenerateContentTeamSummary}
											onSkip={handleSkipContentTeamSummary}
											onSubmit={handleSubmitContentTeamNote}
										/>
									)}

									{/* Account Profile (AI Custom Content) Tab */}
									{activeConfirmationTab === 'accountProfile' && (
										<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
											{!generatedBusinessDescription ? (
												<div className={css(styleSheet.confirmationPanel)}>
													<h3 className={css(styleSheet.confirmationPanelTitle)}>AI Custom Content Preferences</h3>

													<div className={css(styleSheet.deepResearchFields)}>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Website URL</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={accountProfileWebsiteUrl}
																onChange={e => setAccountProfileWebsiteUrl(e.target.value)}
																placeholder='e.g., https://example.com'
																disabled={isGeneratingAccountProfile}
															/>
														</div>
													</div>

													<div className={css(styleSheet.confirmationActions)}>
														<Button
															variant='secondary'
															onClick={handleSkipAccountProfile}
															disabled={isGeneratingAccountProfile}
														>
															Skip
														</Button>
														<Button
															variant='success'
															onClick={handleGenerateAccountProfile}
															isLoading={isGeneratingAccountProfile}
															disabled={isGeneratingAccountProfile}
														>
															Generate Preferences
														</Button>
													</div>
												</div>
											) : (
												<>
													<div className={css(styleSheet.summaryHeader)}>
														<h3>AI Custom Content Preferences</h3>
													</div>
													<div className={css(styleSheet.summaryNote)}>
														Review and edit the generated preferences below, then click Save to store them.
													</div>

													<div className={css(styleSheet.accountProfileFields)}>
														<div className={css(styleSheet.accountProfileFieldGrid)}>
															<div className={css(styleSheet.accountProfileField)}>
																<label className={css(styleSheet.accountProfileFieldLabel)}>Location</label>
																<input
																	className={css(styleSheet.accountProfileFieldInput)}
																	value={generatedLocation}
																	onChange={e => setGeneratedLocation(e.target.value)}
																	disabled={isSavingAccountProfile}
																/>
															</div>
															<div className={css(styleSheet.accountProfileField)}>
																<label className={css(styleSheet.accountProfileFieldLabel)}>Address</label>
																<input
																	className={css(styleSheet.accountProfileFieldInput)}
																	value={generatedAddress}
																	onChange={e => setGeneratedAddress(e.target.value)}
																	disabled={isSavingAccountProfile}
																/>
															</div>
															<div className={css(styleSheet.accountProfileField)}>
																<label className={css(styleSheet.accountProfileFieldLabel)}>Phone Number</label>
																<input
																	className={css(styleSheet.accountProfileFieldInput)}
																	value={generatedPhoneNumber}
																	onChange={e => setGeneratedPhoneNumber(e.target.value)}
																	disabled={isSavingAccountProfile}
																/>
															</div>
															<div className={css(styleSheet.accountProfileField)}>
																<label className={css(styleSheet.accountProfileFieldLabel)}>Pronouns</label>
																<input
																	className={css(styleSheet.accountProfileFieldInput)}
																	value={generatedPronouns}
																	onChange={e => setGeneratedPronouns(e.target.value)}
																	disabled={isSavingAccountProfile}
																/>
															</div>
															<div className={css(styleSheet.accountProfileField)}>
																<label className={css(styleSheet.accountProfileFieldLabel)}>Tone Preference</label>
																<input
																	className={css(styleSheet.accountProfileFieldInput)}
																	value={generatedTonePreference}
																	onChange={e => setGeneratedTonePreference(e.target.value)}
																	disabled={isSavingAccountProfile}
																/>
															</div>
														</div>

														<div className={css(styleSheet.accountProfileField)}>
															<label className={css(styleSheet.accountProfileFieldLabel)}>Business Description</label>
															<textarea
																className={css(styleSheet.accountProfileTextarea)}
																value={generatedBusinessDescription}
																onChange={e => setGeneratedBusinessDescription(e.target.value)}
																rows={6}
																disabled={isSavingAccountProfile}
															/>
														</div>

														<div className={css(styleSheet.accountProfileField)}>
															<label className={css(styleSheet.accountProfileFieldLabel)}>Content Goals</label>
															<textarea
																className={css(styleSheet.accountProfileTextarea)}
																value={generatedContentGoals}
																onChange={e => setGeneratedContentGoals(e.target.value)}
																rows={2}
																disabled={isSavingAccountProfile}
															/>
														</div>

														<div className={css(styleSheet.accountProfileField)}>
															<label className={css(styleSheet.accountProfileFieldLabel)}>Keywords</label>
															<textarea
																className={css(styleSheet.accountProfileTextarea)}
																value={generatedKeywords}
																onChange={e => setGeneratedKeywords(e.target.value)}
																rows={2}
																disabled={isSavingAccountProfile}
															/>
														</div>
													</div>

													<div className={css(styleSheet.formActions)}>
														<button
															className={css(styleSheet.saveButton)}
															onClick={handleSaveAccountProfile}
															disabled={isSavingAccountProfile || !generatedBusinessDescription}
														>
															{isSavingAccountProfile ? 'Saving...' : 'Save Preferences'}
														</button>
													</div>
												</>
											)}
										</div>
									)}

									{/* Brand Settings Tab */}
									{activeConfirmationTab === 'brandSettings' && selectedAccount && (
										<BrandSettingsTab
											account={selectedAccount}
											websiteUrl={brandSettingsWebsiteUrl}
											onWebsiteUrlChange={setBrandSettingsWebsiteUrl}
											onSaveWebsiteUrl={handleSaveBrandSettingsWebsiteUrl}
											isSavingWebsiteUrl={isSavingBrandSettingsWebsiteUrl}
											isWebsiteUrlSaved={brandSettingsWebsiteUrlSaved}
											onDone={handleCompleteBrandSettings}
										/>
									)}

									{/* Industry & Subvertical Tab */}
									{activeConfirmationTab === 'subverticalSettings' && selectedAccount && (
										<SubverticalSettingsTab account={selectedAccount} onDone={handleCompleteSubverticalSettings} />
									)}

									{/* Recap Email Tab */}
									{activeConfirmationTab === 'recapEmail' && (
										<div className={css(styleSheet.confirmationColumn, styleSheet.confirmationColumnFull)}>
											{!recapEmailContentState ? (
												<div className={css(styleSheet.confirmationPanel)}>
													<h3 className={css(styleSheet.confirmationPanelTitle)}>Send Recap Email</h3>
													<div className={css(styleSheet.confirmationText, styleSheet.confirmationTextWithMargin)}>
														Generate a recap email to send to meeting attendees summarizing the discussion.
													</div>

													<div className={css(styleSheet.confirmationActions)}>
														<Button
															variant='secondary'
															onClick={handleSkipRecapEmail}
															disabled={isGeneratingRecapEmail}
														>
															Skip
														</Button>
														<Button
															variant='success'
															onClick={handleGenerateRecapEmail}
															isLoading={isGeneratingRecapEmail}
															disabled={isGeneratingRecapEmail}
														>
															Generate Recap Email
														</Button>
													</div>
												</div>
											) : (
												<>
													<div className={css(styleSheet.deepResearchFields)}>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>
																To (comma or semicolon separated)
															</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={recapEmailRecipients}
																onChange={e => setRecapEmailRecipients(e.target.value)}
																placeholder='email1@example.com, email2@example.com'
																disabled={isSendingRecapEmail}
															/>
														</div>
														<div>
															<label className={css(styleSheet.deepResearchFieldLabel)}>Subject</label>
															<input
																type='text'
																className={css(styleSheet.deepResearchFieldInput)}
																value={recapEmailSubject}
																onChange={e => setRecapEmailSubject(e.target.value)}
																disabled={isSendingRecapEmail}
															/>
														</div>
													</div>

													<div className={css(styleSheet.recapEmailEditorContainer)}>
														<RichContentDocumentEditor
															contentState={recapEmailContentState}
															onContentStateChanged={setRecapEmailContentState}
															readOnly={isSendingRecapEmail}
															config={{
																autoresizeToFitContent: true,
																minHeight: 300,
																contentHorizontalPadding: 20,
																toolbar: 'undo redo | bold italic underline | bullist numlist',
																contentRawCss: RecapEmailEditorCSS,
															}}
														/>
													</div>

													<div className={css(styleSheet.formActions)}>
														<button
															className={css(styleSheet.cancelButton)}
															onClick={handleSkipRecapEmail}
															disabled={isSendingRecapEmail}
														>
															Skip
														</button>
														<button
															className={css(styleSheet.saveButton)}
															onClick={handleSendRecapEmail}
															disabled={
																isSendingRecapEmail ||
																!recapEmailContentState?.hasContent() ||
																!recapEmailSubject ||
																!recapEmailRecipients
															}
														>
															{isSendingRecapEmail ? 'Sending...' : 'Send Email'}
														</button>
													</div>
												</>
											)}
										</div>
									)}
								</div>
							</div>
						)}
					</DialogBody>
				</DialogPopup>
			</Dialog>

			{/* No Show Dialog */}
			<Dialog
				open={isNoShowDialogOpen}
				onOpenChange={open => {
					setIsNoShowDialogOpen(open);
					if (!open) {
						// Reset state when dialog closes
						setNoShowMeeting(null);
						setNoShowAccount(null);
						setNoShowAccountSearchValue('');
					}
				}}
			>
				<DialogPopup styles={[styleSheet.noShowDialog]}>
					<DialogHeader>
						<DialogTitle>Mark No Show</DialogTitle>
					</DialogHeader>
					<DialogBody styles={[styleSheet.noShowDialogBody]}>
						{noShowMeeting && (
							<>
								<div className={css(styleSheet.transcriptPreview)}>
									<div className={css(styleSheet.transcriptPreviewTitle)}>
										{noShowMeeting.summary || 'Untitled Meeting'}
									</div>
									<div className={css(styleSheet.transcriptPreviewContent)}>
										{formatTime(noShowMeeting.startTime)} • {noShowMeeting.durationMinutes} min
									</div>
								</div>

								{/* Account Selection */}
								<div className={css(styleSheet.noShowFormField)}>
									<label className={css(styleSheet.noShowFormLabel)}>
										Account <span className={css(styleSheet.required)}>*</span>
									</label>
									{noShowAccount ? (
										<div className={css(styleSheet.selectedAccountDisplay)}>
											<span>{noShowAccount.companyName}</span>
											<button
												className={css(styleSheet.changeAccountButton)}
												onClick={() => {
													setNoShowAccount(null);
													setNoShowAccountSearchValue('');
												}}
											>
												Change
											</button>
										</div>
									) : (
										<Combobox
											items={accounts}
											value={noShowAccount}
											onValueChange={setNoShowAccount}
											inputValue={noShowAccountSearchValue}
											onInputValueChange={handleNoShowAccountSearchValueChange}
											itemToStringLabel={(account: IAccount) => account.companyName || ''}
											isItemEqualToValue={(item: IAccount, value: IAccount) => item.id === value.id}
										>
											<ComboboxInput placeholder='Search for account...' />
											<ComboboxContent>
												<ComboboxStatus>
													{accountsQuery.isLoading
														? 'Searching...'
														: accounts.length === 0 && noShowAccountSearchValue
															? `No accounts found for "${noShowAccountSearchValue}"`
															: accounts.length > 0
																? `${accounts.length} account${accounts.length === 1 ? '' : 's'} found`
																: 'Start typing to search accounts...'}
												</ComboboxStatus>
												<ComboboxList>
													{(account: IAccount) => (
														<ComboboxAvatarItem key={account.id} value={account}>
															<ComboboxAvatarItemAvatar
																imageUrl={''}
																fallbackText={account.companyName?.substring(0, 2).toUpperCase()}
															/>
															<ComboboxAvatarItemTitle>{account.companyName}</ComboboxAvatarItemTitle>
														</ComboboxAvatarItem>
													)}
												</ComboboxList>
											</ComboboxContent>
										</Combobox>
									)}
								</div>

								{/* Dialog Actions */}
								<div className={css(styleSheet.noShowDialogActions)}>
									<button
										className={css(styleSheet.cancelButton)}
										onClick={() => setIsNoShowDialogOpen(false)}
										disabled={isSavingNoShow}
									>
										Cancel
									</button>
									<button className={css(styleSheet.saveButton)} onClick={handleSaveNoShow} disabled={isSavingNoShow}>
										{isSavingNoShow ? 'Saving...' : 'Record No Show'}
									</button>
								</div>
							</>
						)}
					</DialogBody>
				</DialogPopup>
			</Dialog>

			{askLevTranscript && (
				<AskLevDialog
					open={!!askLevTranscript}
					onOpenChange={open => {
						if (!open) setAskLevTranscript(null);
					}}
					transcriptContent={askLevTranscript.transcriptContent!}
					meetingTopic={askLevTranscript.meetingTopic}
					attendeeNames={selectedMeetingWithTranscripts?.meeting?.attendees?.map(getAttendeeDisplayName)}
				/>
			)}
		</div>
	);
}
